import React, { useEffect, Suspense, lazy as ReactLazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import TopNavbar from './components/TopNavbar';
import Header from './components/Header';
import DsHomeShell from './components/DsHomeShell';
import './styles/HomePage.css';
import './styles/XmSystem.css';
import BrowseInterestTracker from './components/BrowseInterestTracker';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ToastContainer from './components/ToastContainer';
import ErrorBoundary from './components/ErrorBoundary';
import { CartProvider } from './contexts/CartContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ProfilePage from './pages/ProfilePage';
import { CurrencyProvider } from './contexts/CurrencyContext';
import usePageSeo from './hooks/usePageSeo';
import { isCatalogListingPath } from './config/gameServiceCategories';
import { isBrowserExtensionNoise, isExtensionInterceptedFetchError } from './utils/runtimeNoise';

// Track user IP on app load if authenticated
const trackUserIPOnLoad = () => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    fetch('/api/users/track-ip', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }).catch(() => {
      // Silently fail - IP tracking is not critical
    });
  }
};

// Clear chunk-reload flag after successful load so a future chunk error can trigger one reload again
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(() => {
      sessionStorage.removeItem('chunk-reload-attempted');
    }, 3000);
  });
}

// Retry wrapper for lazy(): retries failed chunk load (e.g. after dev server restart or network blip)
const lazyWithRetry = (importFn, retries = 2, delay = 1500) => {
  return ReactLazy(async () => {
    let lastErr;
    for (let i = 0; i <= retries; i++) {
      try {
        return await importFn();
      } catch (err) {
        lastErr = err;
        if (i < retries) {
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    }
    throw lastErr;
  });
};
const lazy = lazyWithRetry;

// Additional chunk error handling in App (backup to index.js handlers)
if (typeof window !== 'undefined') {
  const handleChunkError = (event) => {
    const errorMessage = event.message || event.error?.message || '';
    const errorName = event.error?.name || '';
    const targetSrc = event.target?.src || event.target?.href || '';
    
    const isChunkError = 
      errorName === 'ChunkLoadError' ||
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Loading CSS chunk') ||
      errorMessage.includes('Loading JS chunk') ||
      (errorMessage.includes('chunk') && errorMessage.includes('failed')) ||
      (targetSrc && (
        targetSrc.includes('/static/css/') || 
        targetSrc.includes('/static/js/') ||
        targetSrc.includes('.chunk.')
      ));

    if (isChunkError && !sessionStorage.getItem('chunk-reload-attempted')) {
      console.warn('Chunk loading error detected in App, reloading page...', event);
      sessionStorage.setItem('chunk-reload-attempted', 'true');
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  window.addEventListener(
    'error',
    (event) => {
      if (!isBrowserExtensionNoise(event.error || event, event)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  window.addEventListener('error', handleChunkError, true);

  window.addEventListener('unhandledrejection', (event) => {
    if (isExtensionInterceptedFetchError(event.reason, event)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const reason = event.reason;
    const errorMessage = reason?.message || '';
    const errorName = reason?.name || '';
    
    const isChunkError = 
      errorName === 'ChunkLoadError' ||
      (errorMessage && (
        errorMessage.includes('Loading chunk') ||
        errorMessage.includes('ChunkLoadError') ||
        errorMessage.includes('Failed to fetch dynamically imported module') ||
        errorMessage.includes('Loading CSS chunk') ||
        errorMessage.includes('Loading JS chunk') ||
        (errorMessage.includes('chunk') && errorMessage.includes('failed'))
      ));

    if (isChunkError && !sessionStorage.getItem('chunk-reload-attempted')) {
      console.warn('Chunk loading error detected in promise rejection (App), reloading page...', reason);
      event.preventDefault();
      sessionStorage.setItem('chunk-reload-attempted', 'true');
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  });
}

// Unified pages for all games
const UnifiedCurrencyPage = lazy(() => import('./pages/UnifiedCurrencyPage'));
const UnifiedAccountsPage = lazy(() => import('./pages/UnifiedAccountsPage'));
const UnifiedItemsPage = lazy(() => import('./pages/UnifiedItemsPage'));
const UnifiedTopUpsPage = lazy(() => import('./pages/UnifiedTopUpsPage'));

// Lazy load all page components for code splitting and faster initial load
const FortnitePage = lazy(() => import('./pages/FortnitePage'));
const FortniteTopUpsPage = lazy(() => import('./pages/FortniteTopUpsPage'));
const FortniteBoostingPage = lazy(() => import('./pages/FortniteBoostingPage'));
const GrandRoleplayPage = lazy(() => import('./pages/GrandRoleplayPage'));
const GrandRoleplayTopUpsPage = lazy(() => import('./pages/GrandRoleplayTopUpsPage'));
const GrandRoleplayBoostingPage = lazy(() => import('./pages/FortniteBoostingPage'));
const ValorantPage = lazy(() => import('./pages/ValorantPage'));
const ValorantTopUpsPage = lazy(() => import('./pages/ValorantTopUpsPage'));
const ValorantBoostingPage = lazy(() => import('./pages/ValorantBoostingPage'));
const BoostingRequestPage = lazy(() => import('./pages/BoostingRequestPage'));
const BoostingOfferCreatePage = lazy(() => import('./pages/BoostingOfferCreatePage'));
const BoostingCheckoutPage = lazy(() => import('./pages/BoostingCheckoutPage'));
const BoostingOffersRedirect = lazy(() => import('./pages/BoostingOffersRedirect'));
const ClashRoyalePage = lazy(() => import('./pages/ClashRoyalePage'));
const ClashRoyaleTopUpsPage = lazy(() => import('./pages/ClashRoyaleTopUpsPage'));
const ClashRoyaleBoostingPage = lazy(() => import('./pages/ClashRoyaleBoostingPage'));
const AnimeVanguardsPage = lazy(() => import('./pages/AnimeVanguardsPage'));
const AnimeVanguardsTopUpsPage = lazy(() => import('./pages/AnimeVanguardsTopUpsPage'));
const AnimeVanguardsBoostingPage = lazy(() => import('./pages/AnimeVanguardsBoostingPage'));
const ApexLegendsPage = lazy(() => import('./pages/ApexLegendsPage'));
const ApexLegendsTopUpsPage = lazy(() => import('./pages/ApexLegendsTopUpsPage'));
const ApexLegendsBoostingPage = lazy(() => import('./pages/ApexLegendsBoostingPage'));
const ArcRaidersPage = lazy(() => import('./pages/ArcRaidersPage'));
const ArcRaidersTopUpsPage = lazy(() => import('./pages/ArcRaidersTopUpsPage'));
const ArcRaidersBoostingPage = lazy(() => import('./pages/ArcRaidersBoostingPage'));
const ArenaBreakoutPage = lazy(() => import('./pages/ArenaBreakoutPage'));
const ArenaBreakoutTopUpsPage = lazy(() => import('./pages/ArenaBreakoutTopUpsPage'));
const ArenaBreakoutBoostingPage = lazy(() => import('./pages/ArenaBreakoutBoostingPage'));
const ArenaBreakoutInfinitePage = lazy(() => import('./pages/ArenaBreakoutInfinitePage'));
const ArenaBreakoutInfiniteTopUpsPage = lazy(() => import('./pages/ArenaBreakoutInfiniteTopUpsPage'));
const ArenaBreakoutInfiniteBoostingPage = lazy(() => import('./pages/ArenaBreakoutInfiniteBoostingPage'));
const BattlefieldPage = lazy(() => import('./pages/BattlefieldPage'));
const BattlefieldBoostingPage = lazy(() => import('./pages/BattlefieldBoostingPage'));
const CODCallOfDutyPage = lazy(() => import('./pages/CODCallOfDutyPage'));
const CODCallOfDutyTopUpsPage = lazy(() => import('./pages/CODCallOfDutyTopUpsPage'));
const CODCallOfDutyBoostingPage = lazy(() => import('./pages/CODCallOfDutyBoostingPage'));
const CounterStrike2Page = lazy(() => import('./pages/CounterStrike2Page'));
const CounterStrike2TopUpsPage = lazy(() => import('./pages/CounterStrike2TopUpsPage'));
const CounterStrike2BoostingPage = lazy(() => import('./pages/CounterStrike2BoostingPage'));
const EscapeFromTarkovPage = lazy(() => import('./pages/EscapeFromTarkovPage'));
const EscapeFromTarkovTopUpsPage = lazy(() => import('./pages/EscapeFromTarkovTopUpsPage'));
const EscapeFromTarkovBoostingPage = lazy(() => import('./pages/EscapeFromTarkovBoostingPage'));
const EASportsFCPage = lazy(() => import('./pages/EASportsFCPage'));
const EASportsFCTopUpsPage = lazy(() => import('./pages/EASportsFCTopUpsPage'));
const EASportsFCBoostingPage = lazy(() => import('./pages/EASportsFCBoostingPage'));
const LeagueOfLegendsPage = lazy(() => import('./pages/LeagueOfLegendsPage'));
const LeagueOfLegendsTopUpsPage = lazy(() => import('./pages/LeagueOfLegendsTopUpsPage'));
const LeagueOfLegendsBoostingPage = lazy(() => import('./pages/LeagueOfLegendsBoostingPage'));
const OldSchoolRuneScapePage = lazy(() => import('./pages/OldSchoolRuneScapePage'));
const OldSchoolRuneScapeBoostingPage = lazy(() => import('./pages/OldSchoolRuneScapeBoostingPage'));
const RainbowSixSiegePage = lazy(() => import('./pages/RainbowSixSiegePage'));
const RainbowSixSiegeTopUpsPage = lazy(() => import('./pages/RainbowSixSiegeTopUpsPage'));
const RainbowSixSiegeBoostingPage = lazy(() => import('./pages/RainbowSixSiegeBoostingPage'));
const RocketLeaguePage = lazy(() => import('./pages/RocketLeaguePage'));
const RocketLeagueTopUpsPage = lazy(() => import('./pages/RocketLeagueTopUpsPage'));
const RocketLeagueBoostingPage = lazy(() => import('./pages/RocketLeagueBoostingPage'));
const RobloxPage = lazy(() => import('./pages/RobloxPage'));
const RobloxTopUpsPage = lazy(() => import('./pages/RobloxTopUpsPage'));
const RobloxBoostingPage = lazy(() => import('./pages/RobloxBoostingPage'));
const WarcraftPage = lazy(() => import('./pages/WarcraftPage'));
const WarcraftTopUpsPage = lazy(() => import('./pages/WarcraftTopUpsPage'));
const WarcraftBoostingPage = lazy(() => import('./pages/WarcraftBoostingPage'));
const MinecraftPage = lazy(() => import('./pages/MinecraftPage'));
const MinecraftTopUpsPage = lazy(() => import('./pages/MinecraftTopUpsPage'));
const MinecraftBoostingPage = lazy(() => import('./pages/MinecraftBoostingPage'));
const PathOfExile2Page = lazy(() => import('./pages/PathOfExile2Page'));
const PathOfExile2TopUpsPage = lazy(() => import('./pages/PathOfExile2TopUpsPage'));
const PathOfExile2BoostingPage = lazy(() => import('./pages/PathOfExile2BoostingPage'));
const DonutSMPPage = lazy(() => import('./pages/DonutSMPPage'));
const DonutSMTCurrencyPage = lazy(() => import('./pages/DonutSMTCurrencyPage'));
const HypixelPage = lazy(() => import('./pages/HypixelPage'));
const HypixelTopUpsPage = lazy(() => import('./pages/HypixelTopUpsPage'));
const HypixelCurrencyPage = lazy(() => import('./pages/HypixelCurrencyPage'));
const EscapeFromTarkovCurrencyPage = lazy(() => import('./pages/EscapeFromTarkovCurrencyPage'));
const EASportsFCCurrencyPage = lazy(() => import('./pages/EASportsFCCurrencyPage'));
const OldSchoolRuneScapeCurrencyPage = lazy(() => import('./pages/OldSchoolRuneScapeCurrencyPage'));
const WarcraftCurrencyPage = lazy(() => import('./pages/WarcraftCurrencyPage'));
const BallPoolCurrencyPage = lazy(() => import('./pages/BallPoolCurrencyPage'));
const AlbionOnlineCurrencyPage = lazy(() => import('./pages/AlbionOnlineCurrencyPage'));
const AnimalCrossingNewHorizonsCurrencyPage = lazy(() => import('./pages/AnimalCrossingNewHorizonsCurrencyPage'));
const BlackDesertOnlineCurrencyPage = lazy(() => import('./pages/BlackDesertOnlineCurrencyPage'));
const DarkAndDarkerCurrencyPage = lazy(() => import('./pages/DarkAndDarkerCurrencyPage'));
const DeltaForceCurrencyPage = lazy(() => import('./pages/DeltaForceCurrencyPage'));
const Diablo4CurrencyPage = lazy(() => import('./pages/Diablo4CurrencyPage'));
const DigimonSuperRumbleCurrencyPage = lazy(() => import('./pages/DigimonSuperRumbleCurrencyPage'));
const EldenRingCurrencyPage = lazy(() => import('./pages/EldenRingCurrencyPage'));
const ElderScrollsOnlineCurrencyPage = lazy(() => import('./pages/ElderScrollsOnlineCurrencyPage'));
const Fallout76CurrencyPage = lazy(() => import('./pages/Fallout76CurrencyPage'));
const FinalFantasyXIVCurrencyPage = lazy(() => import('./pages/FinalFantasyXIVCurrencyPage'));
const FischCurrencyPage = lazy(() => import('./pages/FischCurrencyPage'));
const ForzaHorizon5CurrencyPage = lazy(() => import('./pages/ForzaHorizon5CurrencyPage'));
const GrowAGardenCurrencyPage = lazy(() => import('./pages/GrowAGardenCurrencyPage'));
const LostArkCurrencyPage = lazy(() => import('./pages/LostArkCurrencyPage'));
const MinecraftCurrencyPage = lazy(() => import('./pages/MinecraftCurrencyPage'));
const NewWorldCurrencyPage = lazy(() => import('./pages/NewWorldCurrencyPage'));
const PathOfExileCurrencyPage = lazy(() => import('./pages/PathOfExileCurrencyPage'));
const PathOfExile2CurrencyPage = lazy(() => import('./pages/PathOfExile2CurrencyPage'));
const PetSimulator99CurrencyPage = lazy(() => import('./pages/PetSimulator99CurrencyPage'));
const PetsGoCurrencyPage = lazy(() => import('./pages/PetsGoCurrencyPage'));
const PokemonGoCurrencyPage = lazy(() => import('./pages/PokemonGoCurrencyPage'));
const RobloxCurrencyPage = lazy(() => import('./pages/RobloxCurrencyPage'));
const RuneScape3CurrencyPage = lazy(() => import('./pages/RuneScape3CurrencyPage'));
const RunescapeBondsCurrencyPage = lazy(() => import('./pages/RunescapeBondsCurrencyPage'));
const StarCitizenCurrencyPage = lazy(() => import('./pages/StarCitizenCurrencyPage'));
const SWTORCurrencyPage = lazy(() => import('./pages/SWTORCurrencyPage'));
const TemtemCurrencyPage = lazy(() => import('./pages/TemtemCurrencyPage'));
const ThroneAndLibertyCurrencyPage = lazy(() => import('./pages/ThroneAndLibertyCurrencyPage'));
const ToiletTowerDefenseCurrencyPage = lazy(() => import('./pages/ToiletTowerDefenseCurrencyPage'));
const TorchlightInfiniteCurrencyPage = lazy(() => import('./pages/TorchlightInfiniteCurrencyPage'));
const TroveCurrencyPage = lazy(() => import('./pages/TroveCurrencyPage'));
const WarframeCurrencyPage = lazy(() => import('./pages/WarframeCurrencyPage'));
const WoWClassicCurrencyPage = lazy(() => import('./pages/WoWClassicCurrencyPage'));
const WoWMistsofPandariaCurrencyPage = lazy(() => import('./pages/WoWMistsofPandariaCurrencyPage'));
const BladeSoulNEOCurrencyPage = lazy(() => import('./pages/BladeSoulNEOCurrencyPage'));
const BladeBallCurrencyPage = lazy(() => import('./pages/BladeBallCurrencyPage'));
const CreaturesOfSonariaCurrencyPage = lazy(() => import('./pages/CreaturesOfSonariaCurrencyPage'));
const DCUniverseOnlineCurrencyPage = lazy(() => import('./pages/DCUniverseOnlineCurrencyPage'));
const DofusCurrencyPage = lazy(() => import('./pages/DofusCurrencyPage'));
const DuneAwakeningCurrencyPage = lazy(() => import('./pages/DuneAwakeningCurrencyPage'));
const EASportsFCMobileCurrencyPage = lazy(() => import('./pages/EASportsFCMobileCurrencyPage'));
const EVEEchoesCurrencyPage = lazy(() => import('./pages/EVEEchoesCurrencyPage'));
const EVEOnlineCurrencyPage = lazy(() => import('./pages/EVEOnlineCurrencyPage'));
const FinalFantasyXICurrencyPage = lazy(() => import('./pages/FinalFantasyXICurrencyPage'));
const GrowtopiaCurrencyPage = lazy(() => import('./pages/GrowtopiaCurrencyPage'));
const GuildWars2CurrencyPage = lazy(() => import('./pages/GuildWars2CurrencyPage'));
const LastEpochCurrencyPage = lazy(() => import('./pages/LastEpochCurrencyPage'));
const MaddenNFLCurrencyPage = lazy(() => import('./pages/MaddenNFLCurrencyPage'));
const MapleStoryCurrencyPage = lazy(() => import('./pages/MapleStoryCurrencyPage'));
const MortalOnline2CurrencyPage = lazy(() => import('./pages/MortalOnline2CurrencyPage'));
const OdinValhallaRisingCurrencyPage = lazy(() => import('./pages/OdinValhallaRisingCurrencyPage'));
const CS2Page = lazy(() => import('./pages/CS2Page'));
const CS2BoostingPage = lazy(() => import('./pages/CS2BoostingPage'));
const SignInPage = lazy(() => import('./pages/SignInPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const MyRequests = lazy(() => import('./pages/MyRequests'));
const CartPage = lazy(() => import('./pages/CartPage'));
// Preload ProductDetailPage for instant navigation
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));

// Preload components on hover for faster navigation
if (typeof window !== 'undefined') {
  // Preload ProductDetailPage when user hovers over product cards
  document.addEventListener('mouseover', (e) => {
    const productCard = e.target.closest('.product-item');
    if (productCard) {
      import('./pages/ProductDetailPage').catch(() => {});
    }
  }, { passive: true });

  // Preload OrderDetailPage when user is likely to open an order
  // Helps remove the 1-3s delay caused by lazy-loading the chunk after navigation.
  document.addEventListener('mouseover', (e) => {
    const orderLink = e.target.closest('a[href^="/orders/"]');
    const orderRow = e.target.closest('.order-row');
    if (orderLink || orderRow) {
      import('./pages/OrderDetailPage').catch(() => {});
    }
  }, { passive: true });
  
  // Preload game pages when hovering over navigation links
  document.addEventListener('mouseover', (e) => {
    const link = e.target.closest('a[href^="/fortnite"], a[href^="/valorant"], a[href^="/counter-strike"], a[href^="/roblox"], a[href^="/warcraft"]');
    if (link) {
      const href = link.getAttribute('href');
      if (href?.includes('/fortnite')) {
        import('./pages/FortnitePage').catch(() => {});
      } else if (href?.includes('/valorant')) {
        import('./pages/ValorantPage').catch(() => {});
      }
    }
  }, { passive: true });
}
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ModeratorLogin = lazy(() => import('./pages/ModeratorLogin'));
const ModeratorDashboard = lazy(() => import('./pages/ModeratorDashboard'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const BallPool = lazy(() => import('./pages/BallPoolPage'));
const NightsintheForest = lazy(() => import('./pages/NightsintheForestPage'));
const NightsintheForestTopUpsPage = lazy(() => import('./pages/NightsintheForestTopUpsPage'));
const AdoptMe = lazy(() => import('./pages/AdoptMePage'));
const AlbionOnline = lazy(() => import('./pages/AlbionOnlinePage'));
const AllStarTowerDefenseX = lazy(() => import('./pages/AllStarTowerDefenseXPage'));
const AnimalCrossingNewHorizons = lazy(() => import('./pages/AnimalCrossingNewHorizonsPage'));
const AnimeDefenders = lazy(() => import('./pages/AnimeDefendersPage'));
const ARKSurvivalAscended = lazy(() => import('./pages/ARKSurvivalAscendedPage'));
const Arknights = lazy(() => import('./pages/ArknightsPage'));
const AyakashiRumble = lazy(() => import('./pages/AyakashiRumblePage'));
const Battlenet = lazy(() => import('./pages/BattlenetPage'));
const BlackCloverM = lazy(() => import('./pages/BlackCloverMPage'));
const BlackDesertOnline = lazy(() => import('./pages/BlackDesertOnlinePage'));
const BleachBraveSouls = lazy(() => import('./pages/BleachBraveSoulsPage'));
const BloodStrike = lazy(() => import('./pages/BloodStrikePage'));
const BloxFruits = lazy(() => import('./pages/BloxFruitsPage'));
const BrawlStars = lazy(() => import('./pages/BrawlStarsPage'));
const CallofDuty = lazy(() => import('./pages/CallofDutyPage'));
const CallofDutyMobile = lazy(() => import('./pages/CallofDutyMobilePage'));
const CallOfDutyMobileTopUpsPage = lazy(() => import('./pages/CallOfDutyMobileTopUpsPage'));
const CallOfDutyMobileBoostingPage = lazy(() => import('./pages/CallOfDutyMobileBoostingPage'));
const CarParkingMultiplayer = lazy(() => import('./pages/CarParkingMultiplayerPage'));
const ChaosZeroNightmare = lazy(() => import('./pages/ChaosZeroNightmarePage'));
const ChocottoLand = lazy(() => import('./pages/ChocottoLandPage'));
const ClashofClans = lazy(() => import('./pages/ClashofClansPage'));
const CookieRunKingdom = lazy(() => import('./pages/CookieRunKingdomPage'));
const Crunchyroll = lazy(() => import('./pages/CrunchyrollPage'));
const DarkandDarker = lazy(() => import('./pages/DarkandDarkerPage'));
const DarkOrbit = lazy(() => import('./pages/DarkOrbitPage'));
const DayZ = lazy(() => import('./pages/DayZPage'));
const DeadByDaylight = lazy(() => import('./pages/DeadByDaylightPage'));
const DeadRails = lazy(() => import('./pages/DeadRailsPage'));
const DeltaForce = lazy(() => import('./pages/DeltaForcePage'));
const Destiny2 = lazy(() => import('./pages/Destiny2Page'));
const Diablo4 = lazy(() => import('./pages/Diablo4Page'));
const DiabloImmortal = lazy(() => import('./pages/DiabloImmortalPage'));
const DigimonSuperRumble = lazy(() => import('./pages/DigimonSuperRumblePage'));
const Discord = lazy(() => import('./pages/DiscordPage'));
const Dota2 = lazy(() => import('./pages/Dota2Page'));
const DragonBallLegends = lazy(() => import('./pages/DragonBallLegendsPage'));
const DragonBallZDokkanBattle = lazy(() => import('./pages/DragonBallZDokkanBattlePage'));
const DuetNightAbyss = lazy(() => import('./pages/DuetNightAbyssPage'));
const EFootball = lazy(() => import('./pages/eFootballPage'));
const EldenRing = lazy(() => import('./pages/EldenRingPage'));
const ElderScrollsOnline = lazy(() => import('./pages/ElderScrollsOnlinePage'));
const EpicGames = lazy(() => import('./pages/EpicGamesPage'));
const EpicSeven = lazy(() => import('./pages/EpicSevenPage'));
const EtheriaRestart = lazy(() => import('./pages/EtheriaRestartPage'));
const Fallout76 = lazy(() => import('./pages/Fallout76Page'));
const FateGrandOrder = lazy(() => import('./pages/FateGrandOrderPage'));
const FinalFantasyXIV = lazy(() => import('./pages/FinalFantasyXIVPage'));
const Fisch = lazy(() => import('./pages/FischPage'));
const ForzaHorizon5 = lazy(() => import('./pages/ForzaHorizon5Page'));
const GarenaFreeFire = lazy(() => import('./pages/GarenaFreeFirePage'));
const GenshinImpact = lazy(() => import('./pages/GenshinImpactPage'));
const GirlsFrontline2Exilium = lazy(() => import('./pages/GirlsFrontline2ExiliumPage'));
const GoddessofVictoryNikke = lazy(() => import('./pages/GoddessofVictoryNikkePage'));
const GoldenHoYeah = lazy(() => import('./pages/GoldenHoYeahPage'));
const GranblueFantasy = lazy(() => import('./pages/GranblueFantasyPage'));
const GrandTheftAuto5 = lazy(() => import('./pages/GrandTheftAuto5Page'));
const GrowaGarden = lazy(() => import('./pages/GrowaGardenPage'));
const HayDay = lazy(() => import('./pages/HayDayPage'));
const HonkaiImpact3rd = lazy(() => import('./pages/HonkaiImpact3rdPage'));
const HonkaiStarRail = lazy(() => import('./pages/HonkaiStarRailPage'));
const HouchiShoujo = lazy(() => import('./pages/HouchiShoujoPage'));
const IdentityV = lazy(() => import('./pages/IdentityVPage'));
const Jailbreak = lazy(() => import('./pages/JailbreakPage'));
const JoJosBizarreAdventureOraOraOverdrive = lazy(() => import('./pages/JoJosBizarreAdventureOraOraOverdrivePage'));
const JujutsuInfinite = lazy(() => import('./pages/JujutsuInfinitePage'));
const JujutsuKaisenPhantomParade = lazy(() => import('./pages/JujutsuKaisenPhantomParadePage'));
const KaijuNo8 = lazy(() => import('./pages/KaijuNo8Page'));
const KingLegacy = lazy(() => import('./pages/KingLegacyPage'));
const KnightOnline = lazy(() => import('./pages/KnightOnlinePage'));
const KnivesOut = lazy(() => import('./pages/KnivesOutPage'));
const LastIslandofSurvival = lazy(() => import('./pages/LastIslandofSurvivalPage'));
const LastWarSurvival = lazy(() => import('./pages/LastWarSurvivalPage'));
const LeagueofLegendsWildRift = lazy(() => import('./pages/LeagueofLegendsWildRiftPage'));
const LivlyIsland = lazy(() => import('./pages/LivlyIslandPage'));
const LordsMobile = lazy(() => import('./pages/LordsMobilePage'));
const LostArk = lazy(() => import('./pages/LostArkPage'));
const MadokaMagicaMagiaExedra = lazy(() => import('./pages/MadokaMagicaMagiaExedraPage'));
const MarvelContestofChampions = lazy(() => import('./pages/MarvelContestofChampionsPage'));
const MarvelRivals = lazy(() => import('./pages/MarvelRivalsPage'));
const Metin2 = lazy(() => import('./pages/Metin2Page'));
const MillenniumWarAigis = lazy(() => import('./pages/MillenniumWarAigisPage'));
const MobileLegends = lazy(() => import('./pages/MobileLegendsPage'));
const MonsterHunterNow = lazy(() => import('./pages/MonsterHunterNowPage'));
const MonsterStrike = lazy(() => import('./pages/MonsterStrikePage'));
const Netflix = lazy(() => import('./pages/NetflixPage'));
const NewWorld = lazy(() => import('./pages/NewWorldPage'));
const OnePieceBountyRush = lazy(() => import('./pages/OnePieceBountyRushPage'));
const OnePieceTreasureCruise = lazy(() => import('./pages/OnePieceTreasureCruisePage'));
const Origin = lazy(() => import('./pages/OriginPage'));
const Overwatch2 = lazy(() => import('./pages/Overwatch2Page'));
const PathofExile = lazy(() => import('./pages/PathofExilePage'));
const Persona5ThePhantomX = lazy(() => import('./pages/Persona5ThePhantomXPage'));
const PlantsvsBrainrots = lazy(() => import('./pages/PlantsvsBrainrotsPage'));
const PlayStation = lazy(() => import('./pages/PlayStationPage'));
const Pokecolo = lazy(() => import('./pages/PokecoloPage'));
const PokecoloTwin = lazy(() => import('./pages/PokecoloTwinPage'));
const PokemonGo = lazy(() => import('./pages/PokemonGoPage'));
const PokemonTCGPocket = lazy(() => import('./pages/PokemonTCGPocketPage'));
const PrincessConnectReDive = lazy(() => import('./pages/PrincessConnectReDivePage'));
const ProfessionalBaseballSpiritsA = lazy(() => import('./pages/ProfessionalBaseballSpiritsAPage'));
const ProjectSEKAI = lazy(() => import('./pages/ProjectSEKAIPage'));
const PUBG = lazy(() => import('./pages/PUBGPage'));
const PUBGMobile = lazy(() => import('./pages/PUBGMobilePage'));
const PuzzleandDragons = lazy(() => import('./pages/PuzzleandDragonsPage'));
const RaidShadowLegends = lazy(() => import('./pages/RaidShadowLegendsPage'));
const RiseofKingdoms = lazy(() => import('./pages/RiseofKingdomsPage'));
const RoyaleOnline = lazy(() => import('./pages/RoyaleOnlinePage'));
const RuneScape3 = lazy(() => import('./pages/RuneScape3Page'));
const Rust = lazy(() => import('./pages/RustPage'));
const SDGundamGGenerationETERNAL = lazy(() => import('./pages/SDGundamGGenerationETERNALPage'));
const SeaofThieves = lazy(() => import('./pages/SeaofThievesPage'));
const SevenKnightsReBIRTH = lazy(() => import('./pages/SevenKnightsReBIRTHPage'));
const Shadowverse = lazy(() => import('./pages/ShadowversePage'));
const SilverandBlood = lazy(() => import('./pages/SilverandBloodPage'));
const SkyChildrenoftheLight = lazy(() => import('./pages/SkyChildrenoftheLightPage'));
const Spotify = lazy(() => import('./pages/SpotifyPage'));
const Standoff2 = lazy(() => import('./pages/Standoff2Page'));
const StealaBrainrot = lazy(() => import('./pages/StealaBrainrotPage'));
const StellaSora = lazy(() => import('./pages/StellaSoraPage'));
const SummonersWar = lazy(() => import('./pages/SummonersWarPage'));
const TheFinals = lazy(() => import('./pages/TheFinalsPage'));
const TheForge = lazy(() => import('./pages/TheForgePage'));
const TheSevenDeadlySins = lazy(() => import('./pages/TheSevenDeadlySinsPage'));
const ThroneandLiberty = lazy(() => import('./pages/ThroneandLibertyPage'));
const TowerofFantasy = lazy(() => import('./pages/TowerofFantasyPage'));
const UbisoftConnect = lazy(() => import('./pages/UbisoftConnectPage'));
const UmaMusumePrettyDerby = lazy(() => import('./pages/UmaMusumePrettyDerbyPage'));
const UnVEILtheworld = lazy(() => import('./pages/unVEILtheworldPage'));
const VolleyballLegends = lazy(() => import('./pages/VolleyballLegendsPage'));
const WarThunder = lazy(() => import('./pages/WarThunderPage'));
const Warframe = lazy(() => import('./pages/WarframePage'));
const WartuneUltra = lazy(() => import('./pages/WartuneUltraPage'));
const WatcherofRealms = lazy(() => import('./pages/WatcherofRealmsPage'));
const WhereWindsMeet = lazy(() => import('./pages/WhereWindsMeetPage'));
const WhiteCatProject = lazy(() => import('./pages/WhiteCatProjectPage'));
const WhiteoutSurvival = lazy(() => import('./pages/WhiteoutSurvivalPage'));
const WolfTeam = lazy(() => import('./pages/WolfTeamPage'));
const WorldofTanks = lazy(() => import('./pages/WorldofTanksPage'));
const WorldofWarships = lazy(() => import('./pages/WorldofWarshipsPage'));
const WoWClassic = lazy(() => import('./pages/WoWClassicPage'));
const WoWMistsofPandaria = lazy(() => import('./pages/WoWMistsofPandariaPage'));
const WutheringWaves = lazy(() => import('./pages/WutheringWavesPage'));
const Xbox = lazy(() => import('./pages/XboxPage'));
const YokaiWatchWibbleWobble = lazy(() => import('./pages/YokaiWatchWibbleWobblePage'));
const YuGiOhMasterDuel = lazy(() => import('./pages/YuGiOhMasterDuelPage'));
const ZenlessZoneZero = lazy(() => import('./pages/ZenlessZoneZeroPage'));
const Zula = lazy(() => import('./pages/ZulaPage'));
// Top Ups pages
const AmazonTopUpsPage = lazy(() => import('./pages/AmazonTopUpsPage'));
const BigoLiveTopUpsPage = lazy(() => import('./pages/BigoLiveTopUpsPage'));
const BloodStrikeTopUpsPage = lazy(() => import('./pages/BloodStrikeTopUpsPage'));
const BrawlStarsTopUpsPage = lazy(() => import('./pages/BrawlStarsTopUpsPage'));
const ClashOfClansTopUpsPage = lazy(() => import('./pages/ClashOfClansTopUpsPage'));
const CrunchyrollTopUpsPage = lazy(() => import('./pages/CrunchyrollTopUpsPage'));
const DeadByDaylightTopUpsPage = lazy(() => import('./pages/DeadByDaylightTopUpsPage'));
const DeltaForceTopUpsPage = lazy(() => import('./pages/DeltaForceTopUpsPage'));
const Destiny2TopUpsPage = lazy(() => import('./pages/Destiny2TopUpsPage'));
const EaPlayTopUpsPage = lazy(() => import('./pages/EaPlayTopUpsPage'));
const EfootballTopUpsPage = lazy(() => import('./pages/EfootballTopUpsPage'));
const ElderScrollsOnlineTopUpsPage = lazy(() => import('./pages/ElderScrollsOnlineTopUpsPage'));
const EtheriaRestartTopUpsPage = lazy(() => import('./pages/EtheriaRestartTopUpsPage'));
const GarenaFreeFireTopUpsPage = lazy(() => import('./pages/GarenaFreeFireTopUpsPage'));
const GenshinImpactTopUpsPage = lazy(() => import('./pages/GenshinImpactTopUpsPage'));
const GrowtopiaTopUpsPage = lazy(() => import('./pages/GrowtopiaTopUpsPage'));
const HonkaiStarRailTopUpsPage = lazy(() => import('./pages/HonkaiStarRailTopUpsPage'));
const HonorOfKingsTopUpsPage = lazy(() => import('./pages/HonorOfKingsTopUpsPage'));
const IdentityVTopUpsPage = lazy(() => import('./pages/IdentityVTopUpsPage'));
const LeagueOfLegendsWildRiftTopUpsPage = lazy(() => import('./pages/LeagueOfLegendsWildRiftTopUpsPage'));
const LoveAndDeepspaceTopUpsPage = lazy(() => import('./pages/LoveAndDeepspaceTopUpsPage'));
const MarvelRivalsTopUpsPage = lazy(() => import('./pages/MarvelRivalsTopUpsPage'));
const MiliastraWonderlandTopUpsPage = lazy(() => import('./pages/MiliastraWonderlandTopUpsPage'));
const MobileLegendsTopUpsPage = lazy(() => import('./pages/MobileLegendsTopUpsPage'));
const NintendoTopUpsPage = lazy(() => import('./pages/NintendoTopUpsPage'));
const Overwatch2TopUpsPage = lazy(() => import('./pages/Overwatch2TopUpsPage'));
const PlaystationTopUpsPage = lazy(() => import('./pages/PlaystationTopUpsPage'));
const PokemonGoTopUpsPage = lazy(() => import('./pages/PokemonGoTopUpsPage'));
const PokemonTcgPocketTopUpsPage = lazy(() => import('./pages/PokemonTcgPocketTopUpsPage'));
const PoppoLiveTopUpsPage = lazy(() => import('./pages/PoppoLiveTopUpsPage'));
const PubgTopUpsPage = lazy(() => import('./pages/PubgTopUpsPage'));
const PubgMobileTopUpsPage = lazy(() => import('./pages/PubgMobileTopUpsPage'));
const RainbowSixSiegeXTopUpsPage = lazy(() => import('./pages/RainbowSixSiegeXTopUpsPage'));
const RustTopUpsPage = lazy(() => import('./pages/RustTopUpsPage'));
const SeaOfThievesTopUpsPage = lazy(() => import('./pages/SeaOfThievesTopUpsPage'));
const SpotifyTopUpsPage = lazy(() => import('./pages/SpotifyTopUpsPage'));
const SuperSusTopUpsPage = lazy(() => import('./pages/SuperSusTopUpsPage'));
const TiktokTopUpsPage = lazy(() => import('./pages/TiktokTopUpsPage'));
const WhiteoutSurvivalTopUpsPage = lazy(() => import('./pages/WhiteoutSurvivalTopUpsPage'));
const WorldOfWarcraftTopUpsPage = lazy(() => import('./pages/WorldOfWarcraftTopUpsPage'));
const WutheringWavesTopUpsPage = lazy(() => import('./pages/WutheringWavesTopUpsPage'));
const YoutubeTopUpsPage = lazy(() => import('./pages/YoutubeTopUpsPage'));
const ZenlessZoneZeroTopUpsPage = lazy(() => import('./pages/ZenlessZoneZeroTopUpsPage'));
// Items pages
const AnimeEternalPage = lazy(() => import('./pages/AnimeEternalPage'));
const AnimeFightingSimulatorEndlessPage = lazy(() => import('./pages/AnimeFightingSimulatorEndlessPage'));
const AnimeLastStandPage = lazy(() => import('./pages/AnimeLastStandPage'));
const AnimeRebornPage = lazy(() => import('./pages/AnimeRebornPage'));
const AttackOnTitanRevolutionPage = lazy(() => import('./pages/AttackOnTitanRevolutionPage'));
const BeeSwarmSimulatorPage = lazy(() => import('./pages/BeeSwarmSimulatorPage'));
const BladeBallPage = lazy(() => import('./pages/BladeBallPage'));
const BlueLockRivalsPage = lazy(() => import('./pages/BlueLockRivalsPage'));
const Borderlands4Page = lazy(() => import('./pages/Borderlands4Page'));
const BubbleGumSimulatorInfinityPage = lazy(() => import('./pages/BubbleGumSimulatorInfinityPage'));
const BuildAZooPage = lazy(() => import('./pages/BuildAZooPage'));
const CreaturesOfSonariaPage = lazy(() => import('./pages/CreaturesOfSonariaPage'));
const Diablo2ResurrectedPage = lazy(() => import('./pages/Diablo2ResurrectedPage'));
const DuneAwakeningPage = lazy(() => import('./pages/DuneAwakeningPage'));
const EveOnlinePage = lazy(() => import('./pages/EveOnlinePage'));
const FishItPage = lazy(() => import('./pages/FishItPage'));
const GardenTowerDefensePage = lazy(() => import('./pages/GardenTowerDefensePage'));
const GrandPieceOnlinePage = lazy(() => import('./pages/GrandPieceOnlinePage'));
const GrowtopiaPage = lazy(() => import('./pages/GrowtopiaPage'));
const GuildWars2Page = lazy(() => import('./pages/GuildWars2Page'));
const HuntyZombiePage = lazy(() => import('./pages/HuntyZombiePage'));
const InkGamePage = lazy(() => import('./pages/InkGamePage'));
const MonopolyGoPage = lazy(() => import('./pages/MonopolyGoPage'));
const MurderMystery2Page = lazy(() => import('./pages/MurderMystery2Page'));
const MurderersVsSheriffsDuelsPage = lazy(() => import('./pages/MurderersVsSheriffsDuelsPage'));
const PetSimulator99Page = lazy(() => import('./pages/PetSimulator99Page'));
const PetsGoPage = lazy(() => import('./pages/PetsGoPage'));
const PokemonLegendsZAPage = lazy(() => import('./pages/PokemonLegendsZAPage'));
const RaiseAnimalsPage = lazy(() => import('./pages/RaiseAnimalsPage'));
const RobloxLimitedsPage = lazy(() => import('./pages/RobloxLimitedsPage'));
const RobloxRivalsPage = lazy(() => import('./pages/RobloxRivalsPage'));
const TeamFortress2Page = lazy(() => import('./pages/TeamFortress2Page'));
const TipsJarPage = lazy(() => import('./pages/TipsJarPage'));
const TwitchDropsPage = lazy(() => import('./pages/TwitchDropsPage'));
const UniversalTowerDefensePage = lazy(() => import('./pages/UniversalTowerDefensePage'));
const WorldOfWarcraftPage = lazy(() => import('./pages/WorldOfWarcraftPage'));
// Boosting pages
const BlackDesertOnlineBoostingPage = lazy(() => import('./pages/BlackDesertOnlineBoostingPage'));
const BloxFruitsBoostingPage = lazy(() => import('./pages/BloxFruitsBoostingPage'));
const BrawlStarsBoostingPage = lazy(() => import('./pages/BrawlStarsBoostingPage'));
const ClashOfClansBoostingPage = lazy(() => import('./pages/ClashOfClansBoostingPage'));
const DeadByDaylightBoostingPage = lazy(() => import('./pages/DeadByDaylightBoostingPage'));
const DeadlockBoostingPage = lazy(() => import('./pages/DeadlockBoostingPage'));
const DeltaForceBoostingPage = lazy(() => import('./pages/DeltaForceBoostingPage'));
const Destiny2BoostingPage = lazy(() => import('./pages/Destiny2BoostingPage'));
const Diablo4BoostingPage = lazy(() => import('./pages/Diablo4BoostingPage'));
const DonutsmpBoostingPage = lazy(() => import('./pages/DonutsmpBoostingPage'));
const Dota2BoostingPage = lazy(() => import('./pages/Dota2BoostingPage'));
const ElderScrollsOnlineBoostingPage = lazy(() => import('./pages/ElderScrollsOnlineBoostingPage'));
const FinalFantasyXivBoostingPage = lazy(() => import('./pages/FinalFantasyXivBoostingPage'));
const FischBoostingPage = lazy(() => import('./pages/FischBoostingPage'));
const GenshinImpactBoostingPage = lazy(() => import('./pages/GenshinImpactBoostingPage'));
const LeagueOfLegendsWildRiftBoostingPage = lazy(() => import('./pages/LeagueOfLegendsWildRiftBoostingPage'));
const MarvelRivalsBoostingPage = lazy(() => import('./pages/MarvelRivalsBoostingPage'));
const MobileLegendsBoostingPage = lazy(() => import('./pages/MobileLegendsBoostingPage'));
const MonsterHunterWildsBoostingPage = lazy(() => import('./pages/MonsterHunterWildsBoostingPage'));
const NewWorldBoostingPage = lazy(() => import('./pages/NewWorldBoostingPage'));
const Overwatch2BoostingPage = lazy(() => import('./pages/Overwatch2BoostingPage'));
const PathOfExileBoostingPage = lazy(() => import('./pages/PathOfExileBoostingPage'));
const PokemonGoBoostingPage = lazy(() => import('./pages/PokemonGoBoostingPage'));
const RainbowSixSiegeXBoostingPage = lazy(() => import('./pages/RainbowSixSiegeXBoostingPage'));
const Runescape3BoostingPage = lazy(() => import('./pages/Runescape3BoostingPage'));
const RustBoostingPage = lazy(() => import('./pages/RustBoostingPage'));
const TeamfightTacticsBoostingPage = lazy(() => import('./pages/TeamfightTacticsBoostingPage'));
const TheFirstDescendantBoostingPage = lazy(() => import('./pages/TheFirstDescendantBoostingPage'));
const TheForgeBoostingPage = lazy(() => import('./pages/TheForgeBoostingPage'));
const ThroneAndLibertyBoostingPage = lazy(() => import('./pages/ThroneAndLibertyBoostingPage'));
const WarframeBoostingPage = lazy(() => import('./pages/WarframeBoostingPage'));
const WhereWindsMeetBoostingPage = lazy(() => import('./pages/WhereWindsMeetBoostingPage'));
const WorldOfWarcraftBoostingPage = lazy(() => import('./pages/WorldOfWarcraftBoostingPage'));
const WowClassicBoostingPage = lazy(() => import('./pages/WowClassicBoostingPage'));
const WowMistsOfPandariaBoostingPage = lazy(() => import('./pages/WowMistsOfPandariaBoostingPage'));

// Loading fallback component
const PageLoader = () => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    minHeight: '50vh',
    color: '#e5e5e5',
    fontFamily: 'Gilroy, sans-serif'
  }}>
    <div>Loading...</div>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

/**
 * Keeps title, description, canonical, social tags and JSON-LD in step with the current route.
 * The static HTML is already correct on first paint; this covers client-side navigation.
 */
function SeoManager() {
  usePageSeo();
  return null;
}

// Handle OAuth hash redirects (Supabase redirects with tokens in hash)
// This runs at the App level to catch hash redirects immediately
function OAuthHashHandler() {
  useEffect(() => {
    const handleHashAuth = async () => {
      // Check if we have a pending OAuth from index.js
      const pendingToken = sessionStorage.getItem('oauth_access_token');
      const hash = window.location.hash;
      
      let accessToken = null;
      let refreshToken = null;
      let expiresAt = null;
      let expiresIn = null;
      let error = null;
      let errorDescription = null;
      
      if (pendingToken) {
        // Token was already stored by index.js
        accessToken = pendingToken;
        refreshToken = sessionStorage.getItem('oauth_refresh_token');
        expiresAt = sessionStorage.getItem('oauth_expires_at');
        sessionStorage.removeItem('oauth_pending');
        sessionStorage.removeItem('oauth_access_token');
        sessionStorage.removeItem('oauth_refresh_token');
        sessionStorage.removeItem('oauth_expires_at');
      } else if (hash && hash.includes('access_token=')) {
        // Parse hash parameters
        const hashParams = new URLSearchParams(hash.substring(1));
        accessToken = hashParams.get('access_token');
        refreshToken = hashParams.get('refresh_token');
        expiresAt = hashParams.get('expires_at');
        expiresIn = hashParams.get('expires_in');
        error = hashParams.get('error');
        errorDescription = hashParams.get('error_description');
        
        // Clear hash immediately
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } else {
        return;
      }

      // Prevent multiple processing
      if (sessionStorage.getItem('oauth_processing')) {
        console.log('OAuth already processing, skipping...');
        return;
      }
      sessionStorage.setItem('oauth_processing', 'true');

      console.log('Processing OAuth authentication...', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        expiresAt
      });

      if (error) {
        console.error('OAuth error:', error, errorDescription);
        sessionStorage.removeItem('oauth_processing');
        window.location.href = `/auth?error=${encodeURIComponent(errorDescription || error)}`;
        return;
      }

      if (!accessToken) {
        console.error('No access token available');
        sessionStorage.removeItem('oauth_processing');
        return;
      }

      // Calculate expires_at if not already stored
      let expiresAtTimestamp = localStorage.getItem('expires_at');
      if (!expiresAtTimestamp) {
        if (expiresAt) {
          expiresAtTimestamp = parseInt(expiresAt).toString();
        } else if (expiresIn) {
          expiresAtTimestamp = (Math.floor(Date.now() / 1000) + parseInt(expiresIn)).toString();
        } else {
          expiresAtTimestamp = (Math.floor(Date.now() / 1000) + 3600).toString(); // Default 1 hour
        }
        localStorage.setItem('expires_at', expiresAtTimestamp);
      }
      
      // Ensure session is stored (might already be from index.js)
      if (!localStorage.getItem('auth_token')) {
        localStorage.setItem('auth_token', accessToken);
      }
      if (refreshToken && !localStorage.getItem('refresh_token')) {
        localStorage.setItem('refresh_token', refreshToken);
      }

      console.log('Session stored, fetching user data...');

      // Get user data from backend
      try {
        const response = await fetch('/api/auth/session', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (response.ok && data.user) {
          console.log('User data received:', data.user);
          
          // Store user data
          localStorage.setItem('user_id', data.user.id || '');
          localStorage.setItem('user_email', data.user.email || '');
          localStorage.setItem('user_username', data.user.username || '');
          localStorage.setItem('user_avatar_url', data.user.avatar_url || '');
          if (data.user.created_at) {
            localStorage.setItem('user_created_at', data.user.created_at);
          }
          if (data.user.is_seller !== undefined) {
            localStorage.setItem('user_is_seller', data.user.is_seller ? 'true' : 'false');
          }
          if (data.user.veriff_status !== undefined) {
            localStorage.setItem('user_veriff_status', data.user.veriff_status || '');
          }

          // Dispatch login event immediately and multiple times to ensure it's caught
          window.dispatchEvent(new CustomEvent('user-login'));
          // Dispatch again after a very short delay to ensure all components catch it
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('user-login'));
          }, 10);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('user-login'));
          }, 50);

          // Clear hash and processing flag
          window.location.hash = '';
          sessionStorage.removeItem('oauth_processing');
          
          const returnTo = new URLSearchParams(window.location.search).get('returnTo') || '/';
          
          console.log('Redirecting to:', returnTo);
          
          // Use replace to avoid adding to history
          window.location.replace(returnTo);
        } else {
          throw new Error(data.error || 'Failed to get user data');
        }
      } catch (error) {
        console.error('Failed to process OAuth hash:', error);
        sessionStorage.removeItem('oauth_processing');
        window.location.hash = '';
        window.location.href = `/auth?error=${encodeURIComponent('authentication_failed')}`;
      }
    };

    // Run immediately and also listen for hash changes
    handleHashAuth();
    
    const handleHashChange = () => {
      handleHashAuth();
    };
    
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return null;
}

function AppContent() {
    const location = useLocation();
    const backgroundLocation = location.state?.backgroundLocation;
    const isFullPageAuth = location.pathname === '/auth' && !backgroundLocation;
    const isAdminPage = location.pathname.startsWith('/admin');
    const isModPage = location.pathname.startsWith('/mod');
    const isProfilePage = location.pathname.startsWith('/profile');
    const isCatalog = isCatalogListingPath(location.pathname);
    const isXmChrome = !isFullPageAuth && !isAdminPage && !isModPage;
    const isHomePage = location.pathname === '/';
    const isDsChrome = isXmChrome;

    useEffect(() => {
      document.body.classList.toggle('xm-home', isXmChrome);
      document.body.classList.toggle('xm-catalog', isCatalog);
      document.body.classList.toggle('xm-profile', isProfilePage);
      document.body.classList.toggle('xm-landing', isDsChrome);
      return () => {
        document.body.classList.remove('xm-home', 'xm-catalog', 'xm-profile', 'xm-landing');
      };
    }, [isXmChrome, isCatalog, isProfilePage, isDsChrome]);

  return (
    <div className="App">
            {!isFullPageAuth && !isAdminPage && !isModPage && (
              <>
                {!isXmChrome && (
                <div className="abstract-background">
                  <div className="abstract-shape shape-1"></div>
                  <div className="abstract-shape shape-2"></div>
                  <div className="abstract-shape shape-3"></div>
                  <div className="abstract-shape shape-4"></div>
                </div>
                )}
                                {!isXmChrome && <TopNavbar />}
                {isDsChrome ? <DsHomeShell /> : <Header />}
              </>
            )}
      <OAuthHashHandler />
      <div className="app-content" style={{ paddingTop: (isFullPageAuth || isAdminPage || isModPage || isDsChrome) ? '0' : 'var(--site-chrome-offset)' }}>
        <Suspense fallback={<PageLoader />}>
          <Routes location={backgroundLocation || location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/fortnite" element={<UnifiedAccountsPage />} />
          <Route path="/fortnite/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/fortnite/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/fortnite/top-ups" element={<FortniteTopUpsPage />} />
          <Route path="/fortnite/items" element={<UnifiedItemsPage />} />
          <Route path="/fortnite/boosting" element={<FortniteBoostingPage />} />
          <Route path="/grand-roleplay" element={<GrandRoleplayPage />} />
          <Route path="/grand-roleplay/accounts" element={<GrandRoleplayPage />} />
          <Route path="/grand-roleplay/top-ups" element={<GrandRoleplayTopUpsPage />} />
          <Route path="/grand-roleplay/items" element={<GrandRoleplayPage />} />
          <Route path="/grand-roleplay/boosting" element={<GrandRoleplayBoostingPage />} />
          <Route path="/valorant" element={<ValorantPage />} />
          <Route path="/valorant/accounts" element={<ValorantPage />} />
          <Route path="/valorant/top-ups" element={<ValorantTopUpsPage />} />
          <Route path="/valorant/items" element={<ValorantPage />} />
          <Route path="/valorant/boosting" element={<ValorantBoostingPage />} />
          <Route path="/boosting/requests/:requestId" element={<BoostingRequestPage />} />
          <Route path="/boosting/requests/:requestId/offer" element={<BoostingOfferCreatePage />} />
          <Route path="/boosting/checkout/:orderId" element={<BoostingCheckoutPage />} />
          <Route path="/boosting/offers/:requestId" element={<BoostingOffersRedirect />} />
          <Route path="/clash-royale" element={<UnifiedAccountsPage />} />
          <Route path="/clash-royale/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/clash-royale/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/clash-royale/top-ups" element={<ClashRoyaleTopUpsPage />} />
          <Route path="/clash-royale/items" element={<UnifiedItemsPage />} />
          <Route path="/clash-royale/boosting" element={<ClashRoyaleBoostingPage />} />
          <Route path="/anime-vanguards" element={<UnifiedAccountsPage />} />
          <Route path="/anime-vanguards/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/anime-vanguards/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/anime-vanguards/top-ups" element={<AnimeVanguardsTopUpsPage />} />
          <Route path="/anime-vanguards/items" element={<UnifiedItemsPage />} />
          <Route path="/anime-vanguards/boosting" element={<AnimeVanguardsBoostingPage />} />
          <Route path="/apex-legends" element={<UnifiedAccountsPage />} />
          <Route path="/apex-legends/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/apex-legends/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/apex-legends/top-ups" element={<ApexLegendsTopUpsPage />} />
          <Route path="/apex-legends/items" element={<UnifiedItemsPage />} />
          <Route path="/apex-legends/boosting" element={<ApexLegendsBoostingPage />} />
          <Route path="/arc-raiders" element={<UnifiedAccountsPage />} />
          <Route path="/arc-raiders/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/arc-raiders/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/arc-raiders/top-ups" element={<ArcRaidersTopUpsPage />} />
          <Route path="/arc-raiders/items" element={<UnifiedItemsPage />} />
          <Route path="/arc-raiders/boosting" element={<ArcRaidersBoostingPage />} />
          <Route path="/arena-breakout" element={<ArenaBreakoutPage />} />
          <Route path="/arena-breakout/accounts" element={<ArenaBreakoutPage />} />
          <Route path="/arena-breakout/top-ups" element={<ArenaBreakoutTopUpsPage />} />
          <Route path="/arena-breakout/boosting" element={<ArenaBreakoutBoostingPage />} />
          <Route path="/arena-breakout-infinite" element={<UnifiedAccountsPage />} />
          <Route path="/arena-breakout-infinite/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/arena-breakout-infinite/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/arena-breakout-infinite/top-ups" element={<ArenaBreakoutInfiniteTopUpsPage />} />
          <Route path="/arena-breakout-infinite/items" element={<UnifiedItemsPage />} />
          <Route path="/arena-breakout-infinite/boosting" element={<ArenaBreakoutInfiniteBoostingPage />} />
          <Route path="/battlefield" element={<BattlefieldPage />} />
          <Route path="/battlefield/accounts" element={<BattlefieldPage />} />
          <Route path="/battlefield/items" element={<BattlefieldPage />} />
          <Route path="/battlefield/boosting" element={<BattlefieldBoostingPage />} />
          {/* Call of Duty routes with redirects */}
          <Route path="/call-of-duty" element={<Navigate to="/cod-call-of-duty" replace />} />
          <Route path="/call-of-duty/accounts" element={<Navigate to="/cod-call-of-duty/accounts" replace />} />
          <Route path="/call-of-duty/top-ups" element={<Navigate to="/cod-call-of-duty/top-ups" replace />} />
          <Route path="/call-of-duty/items" element={<Navigate to="/cod-call-of-duty/items" replace />} />
          <Route path="/call-of-duty/boosting" element={<Navigate to="/cod-call-of-duty/boosting" replace />} />
          <Route path="/cod-call-of-duty" element={<CODCallOfDutyPage />} />
          <Route path="/cod-call-of-duty/accounts" element={<CODCallOfDutyPage />} />
          <Route path="/cod-call-of-duty/top-ups" element={<CODCallOfDutyTopUpsPage />} />
          <Route path="/cod-call-of-duty/items" element={<CODCallOfDutyPage />} />
          <Route path="/cod-call-of-duty/boosting" element={<CODCallOfDutyBoostingPage />} />
          <Route path="/counter-strike-2" element={<UnifiedAccountsPage />} />
          <Route path="/counter-strike-2/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/counter-strike-2/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/counter-strike-2/top-ups" element={<CounterStrike2TopUpsPage />} />
          <Route path="/counter-strike-2/items" element={<UnifiedItemsPage />} />
          <Route path="/counter-strike-2/boosting" element={<CounterStrike2BoostingPage />} />
          <Route path="/escape-from-tarkov" element={<UnifiedAccountsPage />} />
          <Route path="/escape-from-tarkov/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/escape-from-tarkov/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/escape-from-tarkov/top-ups" element={<EscapeFromTarkovTopUpsPage />} />
          <Route path="/escape-from-tarkov/items" element={<UnifiedItemsPage />} />
          <Route path="/escape-from-tarkov/boosting" element={<EscapeFromTarkovBoostingPage />} />
          <Route path="/ea-sports-fc" element={<UnifiedAccountsPage />} />
          <Route path="/ea-sports-fc/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/ea-sports-fc/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/ea-sports-fc/top-ups" element={<EASportsFCTopUpsPage />} />
          <Route path="/ea-sports-fc/items" element={<UnifiedItemsPage />} />
          <Route path="/ea-sports-fc/boosting" element={<EASportsFCBoostingPage />} />
          <Route path="/league-of-legends" element={<UnifiedAccountsPage />} />
          <Route path="/league-of-legends/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/league-of-legends/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/league-of-legends/top-ups" element={<LeagueOfLegendsTopUpsPage />} />
          <Route path="/league-of-legends/items" element={<UnifiedItemsPage />} />
          <Route path="/league-of-legends/boosting" element={<LeagueOfLegendsBoostingPage />} />
          <Route path="/old-school-runescape" element={<OldSchoolRuneScapePage />} />
          <Route path="/old-school-runescape/currency" element={<OldSchoolRuneScapeCurrencyPage />} />
          <Route path="/old-school-runescape/accounts" element={<OldSchoolRuneScapePage />} />
          <Route path="/old-school-runescape/items" element={<OldSchoolRuneScapePage />} />
          <Route path="/old-school-runescape/boosting" element={<OldSchoolRuneScapeBoostingPage />} />
          <Route path="/rainbow-six-siege" element={<UnifiedAccountsPage />} />
          <Route path="/rainbow-six-siege/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/rainbow-six-siege/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/rainbow-six-siege/top-ups" element={<RainbowSixSiegeTopUpsPage />} />
          <Route path="/rainbow-six-siege/items" element={<UnifiedItemsPage />} />
          <Route path="/rainbow-six-siege/boosting" element={<RainbowSixSiegeBoostingPage />} />
          <Route path="/rainbow-six-siege-x" element={<UnifiedAccountsPage />} />
          <Route path="/rainbow-six-siege-x/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/rainbow-six-siege-x/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/rainbow-six-siege-x/items" element={<UnifiedItemsPage />} />
          <Route path="/rocket-league" element={<RocketLeaguePage />} />
          <Route path="/rocket-league/accounts" element={<RocketLeaguePage />} />
          <Route path="/rocket-league/top-ups" element={<RocketLeagueTopUpsPage />} />
          <Route path="/rocket-league/boosting" element={<RocketLeagueBoostingPage />} />
          <Route path="/roblox" element={<UnifiedAccountsPage />} />
          <Route path="/roblox/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/roblox/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/roblox/top-ups" element={<RobloxTopUpsPage />} />
          <Route path="/roblox/items" element={<UnifiedItemsPage />} />
          <Route path="/roblox/boosting" element={<RobloxBoostingPage />} />
          <Route path="/warcraft" element={<UnifiedAccountsPage />} />
          <Route path="/warcraft/currency" element={<UnifiedCurrencyPage />} />
          <Route path="/warcraft/accounts" element={<UnifiedAccountsPage />} />
          <Route path="/warcraft/top-ups" element={<WarcraftTopUpsPage />} />
          <Route path="/warcraft/items" element={<UnifiedItemsPage />} />
          <Route path="/warcraft/boosting" element={<WarcraftBoostingPage />} />
          <Route path="/minecraft" element={<MinecraftPage />} />
          <Route path="/minecraft/accounts" element={<MinecraftPage />} />
          <Route path="/minecraft/top-ups" element={<MinecraftTopUpsPage />} />
          <Route path="/minecraft/items" element={<MinecraftPage />} />
          <Route path="/minecraft/boosting" element={<MinecraftBoostingPage />} />
          <Route path="/path-of-exile-2" element={<PathOfExile2Page />} />
          <Route path="/path-of-exile-2/accounts" element={<PathOfExile2Page />} />
          <Route path="/path-of-exile-2/top-ups" element={<PathOfExile2TopUpsPage />} />
          <Route path="/path-of-exile-2/items" element={<PathOfExile2Page />} />
          <Route path="/path-of-exile-2/boosting" element={<PathOfExile2BoostingPage />} />
          <Route path="/donutsmp" element={<DonutSMPPage />} />
          <Route path="/donutsmp/currency" element={<DonutSMTCurrencyPage />} />
          <Route path="/donutsmp/accounts" element={<DonutSMPPage />} />
          <Route path="/donutsmp/items" element={<DonutSMPPage />} />
          <Route path="/hypixel" element={<HypixelPage />} />
          <Route path="/hypixel/currency" element={<HypixelCurrencyPage />} />
          <Route path="/hypixel/accounts" element={<HypixelPage />} />
          <Route path="/hypixel/top-ups" element={<HypixelTopUpsPage />} />
          <Route path="/hypixel/items" element={<HypixelPage />} />
          {/* Currency Pages */}
          <Route path="/albion-online/currency" element={<AlbionOnlineCurrencyPage />} />
          <Route path="/animal-crossing-new-horizons/currency" element={<AnimalCrossingNewHorizonsCurrencyPage />} />
          <Route path="/black-desert-online/currency" element={<BlackDesertOnlineCurrencyPage />} />
          <Route path="/dark-and-darker/currency" element={<DarkAndDarkerCurrencyPage />} />
          <Route path="/delta-force/currency" element={<DeltaForceCurrencyPage />} />
          <Route path="/diablo-4/currency" element={<Diablo4CurrencyPage />} />
          <Route path="/digimon-super-rumble/currency" element={<DigimonSuperRumbleCurrencyPage />} />
          <Route path="/elden-ring/currency" element={<EldenRingCurrencyPage />} />
          <Route path="/elder-scrolls-online/currency" element={<ElderScrollsOnlineCurrencyPage />} />
          <Route path="/fallout-76/currency" element={<Fallout76CurrencyPage />} />
          <Route path="/final-fantasy-xiv/currency" element={<FinalFantasyXIVCurrencyPage />} />
          <Route path="/fisch/currency" element={<FischCurrencyPage />} />
          <Route path="/forza-horizon-5/currency" element={<ForzaHorizon5CurrencyPage />} />
          <Route path="/grow-a-garden/currency" element={<GrowAGardenCurrencyPage />} />
          <Route path="/lost-ark/currency" element={<LostArkCurrencyPage />} />
          <Route path="/minecraft/currency" element={<MinecraftCurrencyPage />} />
          <Route path="/new-world/currency" element={<NewWorldCurrencyPage />} />
          <Route path="/path-of-exile/currency" element={<PathOfExileCurrencyPage />} />
          <Route path="/path-of-exile-2/currency" element={<PathOfExile2CurrencyPage />} />
          <Route path="/pet-simulator-99/currency" element={<PetSimulator99CurrencyPage />} />
          <Route path="/pets-go/currency" element={<PetsGoCurrencyPage />} />
          <Route path="/pokemon-go/currency" element={<PokemonGoCurrencyPage />} />
          <Route path="/roblox/currency" element={<RobloxCurrencyPage />} />
          <Route path="/runescape-3/currency" element={<RuneScape3CurrencyPage />} />
          <Route path="/runescape-bonds/currency" element={<RunescapeBondsCurrencyPage />} />
          <Route path="/star-citizen/currency" element={<StarCitizenCurrencyPage />} />
          <Route path="/swtor/currency" element={<SWTORCurrencyPage />} />
          <Route path="/temtem/currency" element={<TemtemCurrencyPage />} />
          <Route path="/throne-and-liberty/currency" element={<ThroneAndLibertyCurrencyPage />} />
          <Route path="/toilet-tower-defense/currency" element={<ToiletTowerDefenseCurrencyPage />} />
          <Route path="/torchlight-infinite/currency" element={<TorchlightInfiniteCurrencyPage />} />
          <Route path="/trove/currency" element={<TroveCurrencyPage />} />
          <Route path="/warframe/currency" element={<WarframeCurrencyPage />} />
          <Route path="/wow-classic/currency" element={<WoWClassicCurrencyPage />} />
          <Route path="/wow-mists-of-pandaria/currency" element={<WoWMistsofPandariaCurrencyPage />} />
          <Route path="/blade-soul-neo/currency" element={<BladeSoulNEOCurrencyPage />} />
          <Route path="/blade-ball/currency" element={<BladeBallCurrencyPage />} />
          <Route path="/creatures-of-sonaria/currency" element={<CreaturesOfSonariaCurrencyPage />} />
          <Route path="/dc-universe-online/currency" element={<DCUniverseOnlineCurrencyPage />} />
          <Route path="/dofus/currency" element={<DofusCurrencyPage />} />
          <Route path="/dune-awakening/currency" element={<DuneAwakeningCurrencyPage />} />
          <Route path="/ea-sports-fc-mobile/currency" element={<EASportsFCMobileCurrencyPage />} />
          <Route path="/eve-echoes/currency" element={<EVEEchoesCurrencyPage />} />
          <Route path="/eve-online/currency" element={<EVEOnlineCurrencyPage />} />
          <Route path="/final-fantasy-xi/currency" element={<FinalFantasyXICurrencyPage />} />
          <Route path="/growtopia/currency" element={<GrowtopiaCurrencyPage />} />
          <Route path="/guild-wars-2/currency" element={<GuildWars2CurrencyPage />} />
          <Route path="/last-epoch/currency" element={<LastEpochCurrencyPage />} />
          <Route path="/madden-nfl/currency" element={<MaddenNFLCurrencyPage />} />
          <Route path="/maplestory/currency" element={<MapleStoryCurrencyPage />} />
          <Route path="/mortal-online-2/currency" element={<MortalOnline2CurrencyPage />} />
          <Route path="/odin-valhalla-rising/currency" element={<OdinValhallaRisingCurrencyPage />} />
          <Route path="/cs2" element={<CS2Page />} />
          <Route path="/cs2/accounts" element={<CS2Page />} />
          <Route path="/cs2/items" element={<CS2Page />} />
          <Route path="/cs2/boosting" element={<CS2BoostingPage />} />
          <Route path="/8-ball-pool" element={<BallPool />} />
          <Route path="/8-ball-pool/currency" element={<BallPoolCurrencyPage />} />
          <Route path="/8-ball-pool/accounts" element={<BallPool />} />
          <Route path="/8-ball-pool/items" element={<BallPool />} />
          <Route path="/99-nights-in-the-forest" element={<NightsintheForest />} />
          <Route path="/99-nights-in-the-forest/accounts" element={<NightsintheForest />} />
          <Route path="/99-nights-in-the-forest/top-ups" element={<NightsintheForestTopUpsPage />} />
          <Route path="/adopt-me" element={<AdoptMe />} />
          <Route path="/adopt-me/accounts" element={<AdoptMe />} />
          <Route path="/adopt-me/items" element={<AdoptMe />} />
          <Route path="/albion-online" element={<AlbionOnline />} />
          <Route path="/albion-online/accounts" element={<AlbionOnline />} />
          <Route path="/albion-online/items" element={<AlbionOnline />} />
          <Route path="/all-star-tower-defense-x" element={<AllStarTowerDefenseX />} />
          <Route path="/all-star-tower-defense-x/accounts" element={<AllStarTowerDefenseX />} />
          <Route path="/all-star-tower-defense-x/items" element={<AllStarTowerDefenseX />} />
          <Route path="/animal-crossing-new-horizons" element={<AnimalCrossingNewHorizons />} />
          <Route path="/animal-crossing-new-horizons/accounts" element={<AnimalCrossingNewHorizons />} />
          <Route path="/animal-crossing-new-horizons/items" element={<AnimalCrossingNewHorizons />} />
          <Route path="/anime-defenders" element={<AnimeDefenders />} />
          <Route path="/anime-defenders/accounts" element={<AnimeDefenders />} />
          <Route path="/anime-defenders/items" element={<AnimeDefenders />} />
          <Route path="/ark-survival-ascended" element={<ARKSurvivalAscended />} />
          <Route path="/ark-survival-ascended/accounts" element={<ARKSurvivalAscended />} />
          <Route path="/ark-survival-ascended/items" element={<ARKSurvivalAscended />} />
          <Route path="/arknights" element={<Arknights />} />
          <Route path="/arknights/accounts" element={<Arknights />} />
          <Route path="/arknights/items" element={<Arknights />} />
          <Route path="/ayakashi-rumble" element={<AyakashiRumble />} />
          <Route path="/ayakashi-rumble/accounts" element={<AyakashiRumble />} />
          <Route path="/ayakashi-rumble/items" element={<AyakashiRumble />} />
          <Route path="/battlenet" element={<Battlenet />} />
          <Route path="/battlenet/accounts" element={<Battlenet />} />
          <Route path="/battlenet/items" element={<Battlenet />} />
          <Route path="/black-clover-m" element={<BlackCloverM />} />
          <Route path="/black-clover-m/accounts" element={<BlackCloverM />} />
          <Route path="/black-clover-m/items" element={<BlackCloverM />} />
          <Route path="/black-desert-online" element={<BlackDesertOnline />} />
          <Route path="/black-desert-online/accounts" element={<BlackDesertOnline />} />
          <Route path="/black-desert-online/items" element={<BlackDesertOnline />} />
          <Route path="/bleach-brave-souls" element={<BleachBraveSouls />} />
          <Route path="/bleach-brave-souls/accounts" element={<BleachBraveSouls />} />
          <Route path="/bleach-brave-souls/items" element={<BleachBraveSouls />} />
          <Route path="/blood-strike" element={<BloodStrike />} />
          <Route path="/blood-strike/accounts" element={<BloodStrike />} />
          <Route path="/blood-strike/items" element={<BloodStrike />} />
          <Route path="/blox-fruits" element={<BloxFruits />} />
          <Route path="/blox-fruits/accounts" element={<BloxFruits />} />
          <Route path="/blox-fruits/items" element={<BloxFruits />} />
          <Route path="/brawl-stars" element={<BrawlStars />} />
          <Route path="/brawl-stars/accounts" element={<BrawlStars />} />
          <Route path="/brawl-stars/items" element={<BrawlStars />} />
          <Route path="/call-of-duty" element={<CallofDuty />} />
          <Route path="/call-of-duty/accounts" element={<CallofDuty />} />
          <Route path="/call-of-duty/items" element={<CallofDuty />} />
          <Route path="/call-of-duty-mobile" element={<CallofDutyMobile />} />
          <Route path="/call-of-duty-mobile/accounts" element={<CallofDutyMobile />} />
          <Route path="/call-of-duty-mobile/top-ups" element={<CallOfDutyMobileTopUpsPage />} />
          <Route path="/call-of-duty-mobile/items" element={<CallofDutyMobile />} />
          <Route path="/call-of-duty-mobile/boosting" element={<CallOfDutyMobileBoostingPage />} />
          <Route path="/car-parking-multiplayer" element={<CarParkingMultiplayer />} />
          <Route path="/car-parking-multiplayer/accounts" element={<CarParkingMultiplayer />} />
          <Route path="/car-parking-multiplayer/items" element={<CarParkingMultiplayer />} />
          <Route path="/chaos-zero-nightmare" element={<ChaosZeroNightmare />} />
          <Route path="/chaos-zero-nightmare/accounts" element={<ChaosZeroNightmare />} />
          <Route path="/chaos-zero-nightmare/items" element={<ChaosZeroNightmare />} />
          <Route path="/chocotto-land" element={<ChocottoLand />} />
          <Route path="/chocotto-land/accounts" element={<ChocottoLand />} />
          <Route path="/chocotto-land/items" element={<ChocottoLand />} />
          <Route path="/clash-of-clans" element={<ClashofClans />} />
          <Route path="/clash-of-clans/accounts" element={<ClashofClans />} />
          <Route path="/clash-of-clans/items" element={<ClashofClans />} />
          <Route path="/cookie-run-kingdom" element={<CookieRunKingdom />} />
          <Route path="/cookie-run-kingdom/accounts" element={<CookieRunKingdom />} />
          <Route path="/cookie-run-kingdom/items" element={<CookieRunKingdom />} />
          <Route path="/crunchyroll" element={<Crunchyroll />} />
          <Route path="/crunchyroll/accounts" element={<Crunchyroll />} />
          <Route path="/dark-and-darker" element={<DarkandDarker />} />
          <Route path="/dark-and-darker/accounts" element={<DarkandDarker />} />
          <Route path="/dark-and-darker/items" element={<DarkandDarker />} />
          <Route path="/darkorbit" element={<DarkOrbit />} />
          <Route path="/darkorbit/accounts" element={<DarkOrbit />} />
          <Route path="/darkorbit/items" element={<DarkOrbit />} />
          <Route path="/dayz" element={<DayZ />} />
          <Route path="/dayz/accounts" element={<DayZ />} />
          <Route path="/dayz/items" element={<DayZ />} />
          <Route path="/dead-by-daylight" element={<DeadByDaylight />} />
          <Route path="/dead-by-daylight/accounts" element={<DeadByDaylight />} />
          <Route path="/dead-rails" element={<DeadRails />} />
          <Route path="/dead-rails/accounts" element={<DeadRails />} />
          <Route path="/dead-rails/items" element={<DeadRails />} />
          <Route path="/delta-force" element={<DeltaForce />} />
          <Route path="/delta-force/accounts" element={<DeltaForce />} />
          <Route path="/delta-force/items" element={<DeltaForce />} />
          <Route path="/destiny-2" element={<Destiny2 />} />
          <Route path="/destiny-2/accounts" element={<Destiny2 />} />
          <Route path="/destiny-2/items" element={<Destiny2 />} />
          <Route path="/diablo-4" element={<Diablo4 />} />
          <Route path="/diablo-4/accounts" element={<Diablo4 />} />
          <Route path="/diablo-4/items" element={<Diablo4 />} />
          <Route path="/diablo-immortal" element={<DiabloImmortal />} />
          <Route path="/diablo-immortal/accounts" element={<DiabloImmortal />} />
          <Route path="/diablo-immortal/items" element={<DiabloImmortal />} />
          <Route path="/digimon-super-rumble" element={<DigimonSuperRumble />} />
          <Route path="/digimon-super-rumble/accounts" element={<DigimonSuperRumble />} />
          <Route path="/digimon-super-rumble/items" element={<DigimonSuperRumble />} />
          <Route path="/discord" element={<Discord />} />
          <Route path="/discord/accounts" element={<Discord />} />
          <Route path="/discord/items" element={<Discord />} />
          <Route path="/dota-2" element={<Dota2 />} />
          <Route path="/dota-2/accounts" element={<Dota2 />} />
          <Route path="/dota-2/items" element={<Dota2 />} />
          <Route path="/dragon-ball-legends" element={<DragonBallLegends />} />
          <Route path="/dragon-ball-legends/accounts" element={<DragonBallLegends />} />
          <Route path="/dragon-ball-legends/items" element={<DragonBallLegends />} />
          <Route path="/dragon-ball-z-dokkan-battle" element={<DragonBallZDokkanBattle />} />
          <Route path="/dragon-ball-z-dokkan-battle/accounts" element={<DragonBallZDokkanBattle />} />
          <Route path="/dragon-ball-z-dokkan-battle/items" element={<DragonBallZDokkanBattle />} />
          <Route path="/duet-night-abyss" element={<DuetNightAbyss />} />
          <Route path="/duet-night-abyss/accounts" element={<DuetNightAbyss />} />
          <Route path="/duet-night-abyss/items" element={<DuetNightAbyss />} />
          <Route path="/efootball" element={<EFootball />} />
          <Route path="/efootball/accounts" element={<EFootball />} />
          <Route path="/elden-ring" element={<EldenRing />} />
          <Route path="/elden-ring/accounts" element={<EldenRing />} />
          <Route path="/elden-ring/items" element={<EldenRing />} />
          <Route path="/elder-scrolls-online" element={<ElderScrollsOnline />} />
          <Route path="/elder-scrolls-online/accounts" element={<ElderScrollsOnline />} />
          <Route path="/elder-scrolls-online/items" element={<ElderScrollsOnline />} />
          <Route path="/epic-games" element={<EpicGames />} />
          <Route path="/epic-games/accounts" element={<EpicGames />} />
          <Route path="/epic-games/items" element={<EpicGames />} />
          <Route path="/epic-seven" element={<EpicSeven />} />
          <Route path="/epic-seven/accounts" element={<EpicSeven />} />
          <Route path="/epic-seven/items" element={<EpicSeven />} />
          <Route path="/etheria-restart" element={<EtheriaRestart />} />
          <Route path="/etheria-restart/accounts" element={<EtheriaRestart />} />
          <Route path="/etheria-restart/items" element={<EtheriaRestart />} />
          <Route path="/fallout-76" element={<Fallout76 />} />
          <Route path="/fallout-76/accounts" element={<Fallout76 />} />
          <Route path="/fallout-76/items" element={<Fallout76 />} />
          <Route path="/fate-grand-order" element={<FateGrandOrder />} />
          <Route path="/fate-grand-order/accounts" element={<FateGrandOrder />} />
          <Route path="/fate-grand-order/items" element={<FateGrandOrder />} />
          <Route path="/final-fantasy-xiv" element={<FinalFantasyXIV />} />
          <Route path="/final-fantasy-xiv/accounts" element={<FinalFantasyXIV />} />
          <Route path="/final-fantasy-xiv/items" element={<FinalFantasyXIV />} />
          <Route path="/fisch" element={<Fisch />} />
          <Route path="/fisch/accounts" element={<Fisch />} />
          <Route path="/fisch/items" element={<Fisch />} />
          <Route path="/forza-horizon-5" element={<ForzaHorizon5 />} />
          <Route path="/forza-horizon-5/accounts" element={<ForzaHorizon5 />} />
          <Route path="/forza-horizon-5/items" element={<ForzaHorizon5 />} />
          <Route path="/garena-free-fire" element={<GarenaFreeFire />} />
          <Route path="/garena-free-fire/accounts" element={<GarenaFreeFire />} />
          <Route path="/garena-free-fire/items" element={<GarenaFreeFire />} />
          <Route path="/genshin-impact" element={<GenshinImpact />} />
          <Route path="/genshin-impact/accounts" element={<GenshinImpact />} />
          <Route path="/genshin-impact/items" element={<GenshinImpact />} />
          <Route path="/girls-frontline-2-exilium" element={<GirlsFrontline2Exilium />} />
          <Route path="/girls-frontline-2-exilium/accounts" element={<GirlsFrontline2Exilium />} />
          <Route path="/girls-frontline-2-exilium/items" element={<GirlsFrontline2Exilium />} />
          <Route path="/goddess-of-victory-nikke" element={<GoddessofVictoryNikke />} />
          <Route path="/goddess-of-victory-nikke/accounts" element={<GoddessofVictoryNikke />} />
          <Route path="/goddess-of-victory-nikke/items" element={<GoddessofVictoryNikke />} />
          <Route path="/golden-hoyeah" element={<GoldenHoYeah />} />
          <Route path="/golden-hoyeah/accounts" element={<GoldenHoYeah />} />
          <Route path="/golden-hoyeah/items" element={<GoldenHoYeah />} />
          <Route path="/granblue-fantasy" element={<GranblueFantasy />} />
          <Route path="/granblue-fantasy/accounts" element={<GranblueFantasy />} />
          <Route path="/granblue-fantasy/items" element={<GranblueFantasy />} />
          <Route path="/grand-theft-auto-5" element={<GrandTheftAuto5 />} />
          <Route path="/grand-theft-auto-5/accounts" element={<GrandTheftAuto5 />} />
          <Route path="/grand-theft-auto-5/items" element={<GrandTheftAuto5 />} />
          <Route path="/grow-a-garden" element={<GrowaGarden />} />
          <Route path="/grow-a-garden/accounts" element={<GrowaGarden />} />
          <Route path="/grow-a-garden/items" element={<GrowaGarden />} />
          <Route path="/hay-day" element={<HayDay />} />
          <Route path="/hay-day/accounts" element={<HayDay />} />
          <Route path="/hay-day/items" element={<HayDay />} />
          <Route path="/honkai-impact-3rd" element={<HonkaiImpact3rd />} />
          <Route path="/honkai-impact-3rd/accounts" element={<HonkaiImpact3rd />} />
          <Route path="/honkai-impact-3rd/items" element={<HonkaiImpact3rd />} />
          <Route path="/honkai-star-rail" element={<HonkaiStarRail />} />
          <Route path="/honkai-star-rail/accounts" element={<HonkaiStarRail />} />
          <Route path="/houchi-shoujo" element={<HouchiShoujo />} />
          <Route path="/houchi-shoujo/accounts" element={<HouchiShoujo />} />
          <Route path="/houchi-shoujo/items" element={<HouchiShoujo />} />
          <Route path="/identity-v" element={<IdentityV />} />
          <Route path="/jailbreak" element={<Jailbreak />} />
          <Route path="/jailbreak/accounts" element={<Jailbreak />} />
          <Route path="/jailbreak/items" element={<Jailbreak />} />
          <Route path="/jojo-s-bizarre-adventure-ora-ora-overdrive" element={<JoJosBizarreAdventureOraOraOverdrive />} />
          <Route path="/jojo-s-bizarre-adventure-ora-ora-overdrive/accounts" element={<JoJosBizarreAdventureOraOraOverdrive />} />
          <Route path="/jojo-s-bizarre-adventure-ora-ora-overdrive/items" element={<JoJosBizarreAdventureOraOraOverdrive />} />
          <Route path="/jujutsu-infinite" element={<JujutsuInfinite />} />
          <Route path="/jujutsu-infinite/accounts" element={<JujutsuInfinite />} />
          <Route path="/jujutsu-infinite/items" element={<JujutsuInfinite />} />
          <Route path="/jujutsu-kaisen-phantom-parade" element={<JujutsuKaisenPhantomParade />} />
          <Route path="/jujutsu-kaisen-phantom-parade/accounts" element={<JujutsuKaisenPhantomParade />} />
          <Route path="/jujutsu-kaisen-phantom-parade/items" element={<JujutsuKaisenPhantomParade />} />
          <Route path="/kaiju-no-8" element={<KaijuNo8 />} />
          <Route path="/kaiju-no-8/accounts" element={<KaijuNo8 />} />
          <Route path="/kaiju-no-8/items" element={<KaijuNo8 />} />
          <Route path="/king-legacy" element={<KingLegacy />} />
          <Route path="/king-legacy/accounts" element={<KingLegacy />} />
          <Route path="/king-legacy/items" element={<KingLegacy />} />
          <Route path="/knight-online" element={<KnightOnline />} />
          <Route path="/knight-online/accounts" element={<KnightOnline />} />
          <Route path="/knight-online/items" element={<KnightOnline />} />
          <Route path="/knives-out" element={<KnivesOut />} />
          <Route path="/knives-out/accounts" element={<KnivesOut />} />
          <Route path="/knives-out/items" element={<KnivesOut />} />
          <Route path="/last-island-of-survival" element={<LastIslandofSurvival />} />
          <Route path="/last-island-of-survival/accounts" element={<LastIslandofSurvival />} />
          <Route path="/last-island-of-survival/items" element={<LastIslandofSurvival />} />
          <Route path="/last-war-survival" element={<LastWarSurvival />} />
          <Route path="/last-war-survival/accounts" element={<LastWarSurvival />} />
          <Route path="/league-of-legends-wild-rift" element={<LeagueofLegendsWildRift />} />
          <Route path="/league-of-legends-wild-rift/accounts" element={<LeagueofLegendsWildRift />} />
          <Route path="/livly-island" element={<LivlyIsland />} />
          <Route path="/livly-island/accounts" element={<LivlyIsland />} />
          <Route path="/livly-island/items" element={<LivlyIsland />} />
          <Route path="/lords-mobile" element={<LordsMobile />} />
          <Route path="/lords-mobile/accounts" element={<LordsMobile />} />
          <Route path="/lords-mobile/items" element={<LordsMobile />} />
          <Route path="/lost-ark" element={<LostArk />} />
          <Route path="/lost-ark/accounts" element={<LostArk />} />
          <Route path="/lost-ark/items" element={<LostArk />} />
          <Route path="/madoka-magica-magia-exedra" element={<MadokaMagicaMagiaExedra />} />
          <Route path="/madoka-magica-magia-exedra/accounts" element={<MadokaMagicaMagiaExedra />} />
          <Route path="/madoka-magica-magia-exedra/items" element={<MadokaMagicaMagiaExedra />} />
          <Route path="/marvel-contest-of-champions" element={<MarvelContestofChampions />} />
          <Route path="/marvel-contest-of-champions/accounts" element={<MarvelContestofChampions />} />
          <Route path="/marvel-contest-of-champions/items" element={<MarvelContestofChampions />} />
          <Route path="/marvel-rivals" element={<MarvelRivals />} />
          <Route path="/marvel-rivals/accounts" element={<MarvelRivals />} />
          <Route path="/marvel-rivals/items" element={<MarvelRivals />} />
          <Route path="/metin2" element={<Metin2 />} />
          <Route path="/metin2/accounts" element={<Metin2 />} />
          <Route path="/metin2/items" element={<Metin2 />} />
          <Route path="/millennium-war-aigis" element={<MillenniumWarAigis />} />
          <Route path="/millennium-war-aigis/accounts" element={<MillenniumWarAigis />} />
          <Route path="/millennium-war-aigis/items" element={<MillenniumWarAigis />} />
          <Route path="/mobile-legends" element={<MobileLegends />} />
          <Route path="/mobile-legends/accounts" element={<MobileLegends />} />
          <Route path="/mobile-legends/items" element={<MobileLegends />} />
          <Route path="/monster-hunter-now" element={<MonsterHunterNow />} />
          <Route path="/monster-hunter-now/accounts" element={<MonsterHunterNow />} />
          <Route path="/monster-hunter-now/items" element={<MonsterHunterNow />} />
          <Route path="/monster-strike" element={<MonsterStrike />} />
          <Route path="/monster-strike/accounts" element={<MonsterStrike />} />
          <Route path="/monster-strike/items" element={<MonsterStrike />} />
          <Route path="/netflix" element={<Netflix />} />
          <Route path="/netflix/accounts" element={<Netflix />} />
          <Route path="/netflix/items" element={<Netflix />} />
          <Route path="/new-world" element={<NewWorld />} />
          <Route path="/new-world/accounts" element={<NewWorld />} />
          <Route path="/new-world/items" element={<NewWorld />} />
          <Route path="/one-piece-bounty-rush" element={<OnePieceBountyRush />} />
          <Route path="/one-piece-bounty-rush/accounts" element={<OnePieceBountyRush />} />
          <Route path="/one-piece-bounty-rush/items" element={<OnePieceBountyRush />} />
          <Route path="/one-piece-treasure-cruise" element={<OnePieceTreasureCruise />} />
          <Route path="/one-piece-treasure-cruise/accounts" element={<OnePieceTreasureCruise />} />
          <Route path="/one-piece-treasure-cruise/items" element={<OnePieceTreasureCruise />} />
          <Route path="/origin" element={<Origin />} />
          <Route path="/origin/accounts" element={<Origin />} />
          <Route path="/origin/items" element={<Origin />} />
          <Route path="/overwatch-2" element={<Overwatch2 />} />
          <Route path="/overwatch-2/accounts" element={<Overwatch2 />} />
          <Route path="/overwatch-2/items" element={<Overwatch2 />} />
          <Route path="/path-of-exile" element={<PathofExile />} />
          <Route path="/path-of-exile/accounts" element={<PathofExile />} />
          <Route path="/path-of-exile/items" element={<PathofExile />} />
          <Route path="/persona-5-the-phantom-x" element={<Persona5ThePhantomX />} />
          <Route path="/persona-5-the-phantom-x/accounts" element={<Persona5ThePhantomX />} />
          <Route path="/persona-5-the-phantom-x/items" element={<Persona5ThePhantomX />} />
          <Route path="/plants-vs-brainrots" element={<PlantsvsBrainrots />} />
          <Route path="/plants-vs-brainrots/accounts" element={<PlantsvsBrainrots />} />
          <Route path="/plants-vs-brainrots/items" element={<PlantsvsBrainrots />} />
          <Route path="/playstation" element={<PlayStation />} />
          <Route path="/playstation/accounts" element={<PlayStation />} />
          <Route path="/pokecolo" element={<Pokecolo />} />
          <Route path="/pokecolo/accounts" element={<Pokecolo />} />
          <Route path="/pokecolo/items" element={<Pokecolo />} />
          <Route path="/pokecolo-twin" element={<PokecoloTwin />} />
          <Route path="/pokecolo-twin/accounts" element={<PokecoloTwin />} />
          <Route path="/pokecolo-twin/items" element={<PokecoloTwin />} />
          <Route path="/pokemon-go" element={<PokemonGo />} />
          <Route path="/pokemon-go/accounts" element={<PokemonGo />} />
          <Route path="/pokemon-go/items" element={<PokemonGo />} />
          <Route path="/pokemon-tcg-pocket" element={<PokemonTCGPocket />} />
          <Route path="/pokemon-tcg-pocket/accounts" element={<PokemonTCGPocket />} />
          <Route path="/pokemon-tcg-pocket/items" element={<PokemonTCGPocket />} />
          <Route path="/princess-connect-re-dive" element={<PrincessConnectReDive />} />
          <Route path="/princess-connect-re-dive/accounts" element={<PrincessConnectReDive />} />
          <Route path="/princess-connect-re-dive/items" element={<PrincessConnectReDive />} />
          <Route path="/professional-baseball-spirits-a" element={<ProfessionalBaseballSpiritsA />} />
          <Route path="/professional-baseball-spirits-a/accounts" element={<ProfessionalBaseballSpiritsA />} />
          <Route path="/professional-baseball-spirits-a/items" element={<ProfessionalBaseballSpiritsA />} />
          <Route path="/project-sekai" element={<ProjectSEKAI />} />
          <Route path="/project-sekai/accounts" element={<ProjectSEKAI />} />
          <Route path="/project-sekai/items" element={<ProjectSEKAI />} />
          <Route path="/pubg" element={<PUBG />} />
          <Route path="/pubg/accounts" element={<PUBG />} />
          <Route path="/pubg/items" element={<PUBG />} />
          <Route path="/pubg-mobile" element={<PUBGMobile />} />
          <Route path="/pubg-mobile/accounts" element={<PUBGMobile />} />
          <Route path="/pubg-mobile/items" element={<PUBGMobile />} />
          <Route path="/puzzle-and-dragons" element={<PuzzleandDragons />} />
          <Route path="/puzzle-and-dragons/accounts" element={<PuzzleandDragons />} />
          <Route path="/puzzle-and-dragons/items" element={<PuzzleandDragons />} />
          <Route path="/raid-shadow-legends" element={<RaidShadowLegends />} />
          <Route path="/raid-shadow-legends/accounts" element={<RaidShadowLegends />} />
          <Route path="/raid-shadow-legends/items" element={<RaidShadowLegends />} />
          <Route path="/rise-of-kingdoms" element={<RiseofKingdoms />} />
          <Route path="/rise-of-kingdoms/accounts" element={<RiseofKingdoms />} />
          <Route path="/rise-of-kingdoms/items" element={<RiseofKingdoms />} />
          <Route path="/royale-online" element={<RoyaleOnline />} />
          <Route path="/royale-online/accounts" element={<RoyaleOnline />} />
          <Route path="/royale-online/items" element={<RoyaleOnline />} />
          <Route path="/runescape-3" element={<RuneScape3 />} />
          <Route path="/runescape-3/accounts" element={<RuneScape3 />} />
          <Route path="/runescape-3/items" element={<RuneScape3 />} />
          <Route path="/rust" element={<Rust />} />
          <Route path="/rust/accounts" element={<Rust />} />
          <Route path="/rust/items" element={<Rust />} />
          <Route path="/sd-gundam-g-generation-eternal" element={<SDGundamGGenerationETERNAL />} />
          <Route path="/sd-gundam-g-generation-eternal/accounts" element={<SDGundamGGenerationETERNAL />} />
          <Route path="/sd-gundam-g-generation-eternal/items" element={<SDGundamGGenerationETERNAL />} />
          <Route path="/sea-of-thieves" element={<SeaofThieves />} />
          <Route path="/sea-of-thieves/accounts" element={<SeaofThieves />} />
          <Route path="/seven-knights-re-birth" element={<SevenKnightsReBIRTH />} />
          <Route path="/seven-knights-re-birth/accounts" element={<SevenKnightsReBIRTH />} />
          <Route path="/seven-knights-re-birth/items" element={<SevenKnightsReBIRTH />} />
          <Route path="/shadowverse" element={<Shadowverse />} />
          <Route path="/shadowverse/accounts" element={<Shadowverse />} />
          <Route path="/shadowverse/items" element={<Shadowverse />} />
          <Route path="/silver-and-blood" element={<SilverandBlood />} />
          <Route path="/silver-and-blood/accounts" element={<SilverandBlood />} />
          <Route path="/silver-and-blood/items" element={<SilverandBlood />} />
          <Route path="/sky-children-of-the-light" element={<SkyChildrenoftheLight />} />
          <Route path="/sky-children-of-the-light/accounts" element={<SkyChildrenoftheLight />} />
          <Route path="/sky-children-of-the-light/items" element={<SkyChildrenoftheLight />} />
          <Route path="/spotify" element={<Spotify />} />
          <Route path="/spotify/accounts" element={<Spotify />} />
          <Route path="/standoff-2" element={<Standoff2 />} />
          <Route path="/standoff-2/accounts" element={<Standoff2 />} />
          <Route path="/standoff-2/items" element={<Standoff2 />} />
          <Route path="/steal-a-brainrot" element={<StealaBrainrot />} />
          <Route path="/steal-a-brainrot/accounts" element={<StealaBrainrot />} />
          <Route path="/steal-a-brainrot/items" element={<StealaBrainrot />} />
          <Route path="/stella-sora" element={<StellaSora />} />
          <Route path="/stella-sora/accounts" element={<StellaSora />} />
          <Route path="/stella-sora/items" element={<StellaSora />} />
          <Route path="/summoners-war" element={<SummonersWar />} />
          <Route path="/summoners-war/accounts" element={<SummonersWar />} />
          <Route path="/summoners-war/items" element={<SummonersWar />} />
          <Route path="/the-finals" element={<TheFinals />} />
          <Route path="/the-finals/accounts" element={<TheFinals />} />
          <Route path="/the-finals/items" element={<TheFinals />} />
          <Route path="/the-forge" element={<TheForge />} />
          <Route path="/the-forge/accounts" element={<TheForge />} />
          <Route path="/the-forge/items" element={<TheForge />} />
          <Route path="/the-seven-deadly-sins" element={<TheSevenDeadlySins />} />
          <Route path="/the-seven-deadly-sins/accounts" element={<TheSevenDeadlySins />} />
          <Route path="/the-seven-deadly-sins/items" element={<TheSevenDeadlySins />} />
          <Route path="/throne-and-liberty" element={<ThroneandLiberty />} />
          <Route path="/throne-and-liberty/accounts" element={<ThroneandLiberty />} />
          <Route path="/throne-and-liberty/items" element={<ThroneandLiberty />} />
          <Route path="/tower-of-fantasy" element={<TowerofFantasy />} />
          <Route path="/tower-of-fantasy/accounts" element={<TowerofFantasy />} />
          <Route path="/tower-of-fantasy/items" element={<TowerofFantasy />} />
          <Route path="/ubisoft-connect" element={<UbisoftConnect />} />
          <Route path="/ubisoft-connect/accounts" element={<UbisoftConnect />} />
          <Route path="/ubisoft-connect/items" element={<UbisoftConnect />} />
          <Route path="/uma-musume-pretty-derby" element={<UmaMusumePrettyDerby />} />
          <Route path="/uma-musume-pretty-derby/accounts" element={<UmaMusumePrettyDerby />} />
          <Route path="/uma-musume-pretty-derby/items" element={<UmaMusumePrettyDerby />} />
          <Route path="/unveil-the-world" element={<UnVEILtheworld />} />
          <Route path="/unveil-the-world/accounts" element={<UnVEILtheworld />} />
          <Route path="/unveil-the-world/items" element={<UnVEILtheworld />} />
          <Route path="/volleyball-legends" element={<VolleyballLegends />} />
          <Route path="/volleyball-legends/accounts" element={<VolleyballLegends />} />
          <Route path="/volleyball-legends/items" element={<VolleyballLegends />} />
          <Route path="/war-thunder" element={<WarThunder />} />
          <Route path="/war-thunder/accounts" element={<WarThunder />} />
          <Route path="/war-thunder/items" element={<WarThunder />} />
          <Route path="/warframe" element={<Warframe />} />
          <Route path="/warframe/accounts" element={<Warframe />} />
          <Route path="/warframe/items" element={<Warframe />} />
          <Route path="/wartune-ultra" element={<WartuneUltra />} />
          <Route path="/wartune-ultra/accounts" element={<WartuneUltra />} />
          <Route path="/wartune-ultra/items" element={<WartuneUltra />} />
          <Route path="/watcher-of-realms" element={<WatcherofRealms />} />
          <Route path="/watcher-of-realms/accounts" element={<WatcherofRealms />} />
          <Route path="/watcher-of-realms/items" element={<WatcherofRealms />} />
          <Route path="/where-winds-meet" element={<WhereWindsMeet />} />
          <Route path="/where-winds-meet/accounts" element={<WhereWindsMeet />} />
          <Route path="/where-winds-meet/items" element={<WhereWindsMeet />} />
          <Route path="/white-cat-project" element={<WhiteCatProject />} />
          <Route path="/white-cat-project/accounts" element={<WhiteCatProject />} />
          <Route path="/white-cat-project/items" element={<WhiteCatProject />} />
          <Route path="/whiteout-survival" element={<WhiteoutSurvival />} />
          <Route path="/whiteout-survival/accounts" element={<WhiteoutSurvival />} />
          <Route path="/wolfteam" element={<WolfTeam />} />
          <Route path="/wolfteam/accounts" element={<WolfTeam />} />
          <Route path="/wolfteam/items" element={<WolfTeam />} />
          <Route path="/world-of-tanks" element={<WorldofTanks />} />
          <Route path="/world-of-tanks/accounts" element={<WorldofTanks />} />
          <Route path="/world-of-tanks/items" element={<WorldofTanks />} />
          <Route path="/world-of-warships" element={<WorldofWarships />} />
          <Route path="/world-of-warships/accounts" element={<WorldofWarships />} />
          <Route path="/world-of-warships/items" element={<WorldofWarships />} />
          <Route path="/wow-classic" element={<WoWClassic />} />
          <Route path="/wow-classic/accounts" element={<WoWClassic />} />
          <Route path="/wow-classic/items" element={<WoWClassic />} />
          <Route path="/wow-mists-of-pandaria" element={<WoWMistsofPandaria />} />
          <Route path="/wow-mists-of-pandaria/accounts" element={<WoWMistsofPandaria />} />
          <Route path="/wow-mists-of-pandaria/items" element={<WoWMistsofPandaria />} />
          <Route path="/wuthering-waves" element={<WutheringWaves />} />
          <Route path="/wuthering-waves/accounts" element={<WutheringWaves />} />
          <Route path="/wuthering-waves/items" element={<WutheringWaves />} />
          <Route path="/xbox" element={<Xbox />} />
          <Route path="/xbox/accounts" element={<Xbox />} />
          <Route path="/xbox/items" element={<Xbox />} />
          <Route path="/yo-kai-watch-wibble-wobble" element={<YokaiWatchWibbleWobble />} />
          <Route path="/yo-kai-watch-wibble-wobble/accounts" element={<YokaiWatchWibbleWobble />} />
          <Route path="/yo-kai-watch-wibble-wobble/items" element={<YokaiWatchWibbleWobble />} />
          <Route path="/yu-gi-oh-master-duel" element={<YuGiOhMasterDuel />} />
          <Route path="/yu-gi-oh-master-duel/accounts" element={<YuGiOhMasterDuel />} />
          <Route path="/yu-gi-oh-master-duel/items" element={<YuGiOhMasterDuel />} />
          <Route path="/zenless-zone-zero" element={<ZenlessZoneZero />} />
          <Route path="/zenless-zone-zero/accounts" element={<ZenlessZoneZero />} />
          <Route path="/zula" element={<Zula />} />
          <Route path="/zula/accounts" element={<Zula />} />
          <Route path="/zula/items" element={<Zula />} />
          {/* Top Ups routes */}
          <Route path="/amazon/top-ups" element={<AmazonTopUpsPage />} />
          <Route path="/bigo-live/top-ups" element={<BigoLiveTopUpsPage />} />
          <Route path="/blood-strike/top-ups" element={<BloodStrikeTopUpsPage />} />
          <Route path="/brawl-stars/top-ups" element={<BrawlStarsTopUpsPage />} />
          <Route path="/clash-of-clans/top-ups" element={<ClashOfClansTopUpsPage />} />
          <Route path="/crunchyroll/top-ups" element={<CrunchyrollTopUpsPage />} />
          <Route path="/dead-by-daylight/top-ups" element={<DeadByDaylightTopUpsPage />} />
          <Route path="/delta-force/top-ups" element={<DeltaForceTopUpsPage />} />
          <Route path="/destiny-2/top-ups" element={<Destiny2TopUpsPage />} />
          <Route path="/ea-play/top-ups" element={<EaPlayTopUpsPage />} />
          <Route path="/efootball/top-ups" element={<EfootballTopUpsPage />} />
          <Route path="/elder-scrolls-online/top-ups" element={<ElderScrollsOnlineTopUpsPage />} />
          <Route path="/etheria-restart/top-ups" element={<EtheriaRestartTopUpsPage />} />
          <Route path="/garena-free-fire/top-ups" element={<GarenaFreeFireTopUpsPage />} />
          <Route path="/genshin-impact/top-ups" element={<GenshinImpactTopUpsPage />} />
          <Route path="/growtopia/top-ups" element={<GrowtopiaTopUpsPage />} />
          <Route path="/honkai-star-rail/top-ups" element={<HonkaiStarRailTopUpsPage />} />
          <Route path="/honor-of-kings/top-ups" element={<HonorOfKingsTopUpsPage />} />
          <Route path="/identity-v/top-ups" element={<IdentityVTopUpsPage />} />
          <Route path="/league-of-legends-wild-rift/top-ups" element={<LeagueOfLegendsWildRiftTopUpsPage />} />
          <Route path="/love-and-deepspace/top-ups" element={<LoveAndDeepspaceTopUpsPage />} />
          <Route path="/marvel-rivals/top-ups" element={<MarvelRivalsTopUpsPage />} />
          <Route path="/miliastra-wonderland/top-ups" element={<MiliastraWonderlandTopUpsPage />} />
          <Route path="/mobile-legends/top-ups" element={<MobileLegendsTopUpsPage />} />
          <Route path="/nintendo/top-ups" element={<NintendoTopUpsPage />} />
          <Route path="/overwatch-2/top-ups" element={<Overwatch2TopUpsPage />} />
          <Route path="/playstation/top-ups" element={<PlaystationTopUpsPage />} />
          <Route path="/pokemon-go/top-ups" element={<PokemonGoTopUpsPage />} />
          <Route path="/pokemon-tcg-pocket/top-ups" element={<PokemonTcgPocketTopUpsPage />} />
          <Route path="/poppo-live/top-ups" element={<PoppoLiveTopUpsPage />} />
          <Route path="/pubg/top-ups" element={<PubgTopUpsPage />} />
          <Route path="/pubg-mobile/top-ups" element={<PubgMobileTopUpsPage />} />
          <Route path="/rainbow-six-siege-x/top-ups" element={<RainbowSixSiegeXTopUpsPage />} />
          <Route path="/rust/top-ups" element={<RustTopUpsPage />} />
          <Route path="/sea-of-thieves/top-ups" element={<SeaOfThievesTopUpsPage />} />
          <Route path="/spotify/top-ups" element={<SpotifyTopUpsPage />} />
          <Route path="/super-sus/top-ups" element={<SuperSusTopUpsPage />} />
          <Route path="/tiktok/top-ups" element={<TiktokTopUpsPage />} />
          <Route path="/whiteout-survival/top-ups" element={<WhiteoutSurvivalTopUpsPage />} />
          <Route path="/world-of-warcraft/accounts" element={<WorldOfWarcraftPage />} />
          <Route path="/world-of-warcraft/top-ups" element={<WorldOfWarcraftTopUpsPage />} />
          <Route path="/wuthering-waves/top-ups" element={<WutheringWavesTopUpsPage />} />
          <Route path="/youtube/top-ups" element={<YoutubeTopUpsPage />} />
          <Route path="/zenless-zone-zero/top-ups" element={<ZenlessZoneZeroTopUpsPage />} />
          {/* Items routes */}
          <Route path="/anime-eternal/items" element={<AnimeEternalPage />} />
          <Route path="/anime-fighting-simulator-endless/items" element={<AnimeFightingSimulatorEndlessPage />} />
          <Route path="/anime-last-stand/items" element={<AnimeLastStandPage />} />
          <Route path="/anime-reborn/items" element={<AnimeRebornPage />} />
          <Route path="/attack-on-titan-revolution/items" element={<AttackOnTitanRevolutionPage />} />
          <Route path="/bee-swarm-simulator/items" element={<BeeSwarmSimulatorPage />} />
          <Route path="/blade-ball/items" element={<BladeBallPage />} />
          <Route path="/blue-lock-rivals/items" element={<BlueLockRivalsPage />} />
          <Route path="/borderlands-4/items" element={<Borderlands4Page />} />
          <Route path="/bubble-gum-simulator-infinity/items" element={<BubbleGumSimulatorInfinityPage />} />
          <Route path="/build-a-zoo/items" element={<BuildAZooPage />} />
          <Route path="/creatures-of-sonaria/items" element={<CreaturesOfSonariaPage />} />
          <Route path="/diablo-2-resurrected/items" element={<Diablo2ResurrectedPage />} />
          <Route path="/dune-awakening/items" element={<DuneAwakeningPage />} />
          <Route path="/eve-online/items" element={<EveOnlinePage />} />
          <Route path="/fish-it/items" element={<FishItPage />} />
          <Route path="/garden-tower-defense/items" element={<GardenTowerDefensePage />} />
          <Route path="/grand-piece-online/items" element={<GrandPieceOnlinePage />} />
          <Route path="/growtopia/items" element={<GrowtopiaPage />} />
          <Route path="/guild-wars-2/items" element={<GuildWars2Page />} />
          <Route path="/hunty-zombie/items" element={<HuntyZombiePage />} />
          <Route path="/ink-game/items" element={<InkGamePage />} />
          <Route path="/monopoly-go/items" element={<MonopolyGoPage />} />
          <Route path="/murder-mystery-2/items" element={<MurderMystery2Page />} />
          <Route path="/murderers-vs-sheriffs-duels/items" element={<MurderersVsSheriffsDuelsPage />} />
          <Route path="/pet-simulator-99/items" element={<PetSimulator99Page />} />
          <Route path="/pets-go/items" element={<PetsGoPage />} />
          <Route path="/pokemon-legends-z-a/items" element={<PokemonLegendsZAPage />} />
          <Route path="/raise-animals/items" element={<RaiseAnimalsPage />} />
          <Route path="/roblox-limiteds/items" element={<RobloxLimitedsPage />} />
          <Route path="/roblox-rivals/items" element={<RobloxRivalsPage />} />
          <Route path="/team-fortress-2/items" element={<TeamFortress2Page />} />
          <Route path="/tips-jar/items" element={<TipsJarPage />} />
          <Route path="/twitch-drops/items" element={<TwitchDropsPage />} />
          <Route path="/universal-tower-defense/items" element={<UniversalTowerDefensePage />} />
          <Route path="/world-of-warcraft/items" element={<WorldOfWarcraftPage />} />
          {/* Boosting routes */}
          <Route path="/black-desert-online/boosting" element={<BlackDesertOnlineBoostingPage />} />
          <Route path="/blox-fruits/boosting" element={<BloxFruitsBoostingPage />} />
          <Route path="/brawl-stars/boosting" element={<BrawlStarsBoostingPage />} />
          <Route path="/clash-of-clans/boosting" element={<ClashOfClansBoostingPage />} />
          <Route path="/dead-by-daylight/boosting" element={<DeadByDaylightBoostingPage />} />
          <Route path="/deadlock/boosting" element={<DeadlockBoostingPage />} />
          <Route path="/delta-force/boosting" element={<DeltaForceBoostingPage />} />
          <Route path="/destiny-2/boosting" element={<Destiny2BoostingPage />} />
          <Route path="/diablo-4/boosting" element={<Diablo4BoostingPage />} />
          <Route path="/donutsmp/boosting" element={<DonutsmpBoostingPage />} />
          <Route path="/dota-2/boosting" element={<Dota2BoostingPage />} />
          <Route path="/elder-scrolls-online/boosting" element={<ElderScrollsOnlineBoostingPage />} />
          <Route path="/final-fantasy-xiv/boosting" element={<FinalFantasyXivBoostingPage />} />
          <Route path="/fisch/boosting" element={<FischBoostingPage />} />
          <Route path="/genshin-impact/boosting" element={<GenshinImpactBoostingPage />} />
          <Route path="/league-of-legends-wild-rift/boosting" element={<LeagueOfLegendsWildRiftBoostingPage />} />
          <Route path="/marvel-rivals/boosting" element={<MarvelRivalsBoostingPage />} />
          <Route path="/mobile-legends/boosting" element={<MobileLegendsBoostingPage />} />
          <Route path="/monster-hunter-wilds/boosting" element={<MonsterHunterWildsBoostingPage />} />
          <Route path="/new-world/boosting" element={<NewWorldBoostingPage />} />
          <Route path="/overwatch-2/boosting" element={<Overwatch2BoostingPage />} />
          <Route path="/path-of-exile/boosting" element={<PathOfExileBoostingPage />} />
          <Route path="/pokemon-go/boosting" element={<PokemonGoBoostingPage />} />
          <Route path="/rainbow-six-siege-x/boosting" element={<RainbowSixSiegeXBoostingPage />} />
          <Route path="/runescape-3/boosting" element={<Runescape3BoostingPage />} />
          <Route path="/rust/boosting" element={<RustBoostingPage />} />
          <Route path="/teamfight-tactics/boosting" element={<TeamfightTacticsBoostingPage />} />
          <Route path="/the-first-descendant/boosting" element={<TheFirstDescendantBoostingPage />} />
          <Route path="/the-forge/boosting" element={<TheForgeBoostingPage />} />
          <Route path="/throne-and-liberty/boosting" element={<ThroneAndLibertyBoostingPage />} />
          <Route path="/warframe/boosting" element={<WarframeBoostingPage />} />
          <Route path="/where-winds-meet/boosting" element={<WhereWindsMeetBoostingPage />} />
          <Route path="/world-of-warcraft/boosting" element={<WorldOfWarcraftBoostingPage />} />
          <Route path="/wow-classic/boosting" element={<WowClassicBoostingPage />} />
          <Route path="/wow-mists-of-pandaria/boosting" element={<WowMistsOfPandariaBoostingPage />} />
          <Route path="/auth" element={<SignInPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/account" element={<ProfilePage />} />
          <Route path="/profile/wallet" element={<ProfilePage />} />
          <Route path="/profile/messages" element={<ProfilePage />} />
          <Route path="/profile/notifications" element={<ProfilePage />} />
          <Route path="/profile/loyalty" element={<ProfilePage />} />
          <Route path="/profile/seller" element={<ProfilePage />} />
          <Route path="/profile/offers" element={<ProfilePage />} />
          <Route path="/profile/offers/currency" element={<ProfilePage />} />
          <Route path="/profile/offers/accounts" element={<ProfilePage />} />
          <Route path="/profile/offers/top-ups" element={<ProfilePage />} />
          <Route path="/profile/offers/items" element={<ProfilePage />} />
          <Route path="/profile/seller" element={<ProfilePage />} />
          <Route path="/profile/seller/purchased-orders" element={<ProfilePage />} />
          <Route path="/profile/seller/sold-orders" element={<ProfilePage />} />
          <Route path="/profile/seller/my-items" element={<ProfilePage />} />
          <Route path="/profile/seller/requests" element={<ProfilePage />} />
          <Route path="/profile/become-seller" element={<ProfilePage />} />
          <Route path="/profile/boosting" element={<ProfilePage />} />
          <Route path="/profile/boosting/my-requests" element={<ProfilePage />} />
          <Route path="/profile/boosting/received" element={<ProfilePage />} />
          <Route path="/profile/feedback" element={<ProfilePage />} />
          <Route path="/profile/view" element={<ProfilePage />} />
          <Route path="/profile/orders" element={<ProfilePage />} />
          <Route path="/profile/orders/purchased" element={<ProfilePage />} />
          <Route path="/profile/orders/sold" element={<ProfilePage />} />
          <Route path="/users/:username" element={<PublicProfilePage />} />
          <Route path="/my-requests" element={<MyRequests />} />
          <Route path="/cart" element={<Navigate to="/" replace />} />
          <Route path="/cart/:itemId" element={<Navigate to="/" replace />} />
          <Route path="/checkout/:adId" element={<CartPage />} />
          <Route path="/ad/:id" element={<ProductDetailPage />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/mod" element={<ModeratorLogin />} />
          <Route path="/mod/dashboard" element={<ModeratorDashboard />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/orders" element={<Navigate to="/profile/orders/purchased" replace />} />
          <Route path="/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        {!isFullPageAuth && !isAdminPage && !isModPage && !isProfilePage && !isHomePage && <Footer />}
      </div>
      {backgroundLocation && (
        <div className="auth-modal-root">
          <Suspense fallback={null}>
            <Routes>
              <Route path="/auth" element={<SignInPage />} />
            </Routes>
          </Suspense>
        </div>
      )}
      <ToastContainer />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <CartProvider>
          <Router>
            <LanguageProvider>
              <CurrencyProvider>
                <ScrollToTop />
                <SeoManager />
                <BrowseInterestTracker />
                <AppContent />
              </CurrencyProvider>
            </LanguageProvider>
          </Router>
        </CartProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
